import { LOCALE_CONFIG } from './config';

export function getUiStrings(locale) {
  const lang = LOCALE_CONFIG[locale]?.lang || 'en';
  const base = STRINGS[lang] || STRINGS.en;
  const nav = NAV_STRINGS[lang] || NAV_STRINGS.en;
  return { ...base, nav };
}

const en = {
  headerTitle: "When will you be debt-free?",
  headerSubtitle: "Enter your debts · Get a personalized plan in seconds",
  yourDebts: "Your Debts",
  colType: "Type", colBalance: "Balance", colRate: "Rate %", colMinPay: "Min Pay",
  placeholderBalance: "Balance", placeholderRate: "Rate %", placeholderMin: "Min",
  selectType: "Select type...", addDebt: "+ Add debt",
  required: "Required",
  minPaymentError: min => `Min payment must be more than ${min}/mo (monthly interest)`,
  extraTitle: "Extra Payment Scenarios",
  conservative: "Conservative", balanced: "Balanced", aggressive: "Aggressive",
  extraHint: sym => `Even ${sym}50 extra/month can save you thousands in interest`,
  totalDebt: "Total debt",
  calculateBtn: "Calculate My Payoff Date",
  freeNoSignup: "Free · No signup · Instant results",
  methodAvalanche: "Avalanche", methodSnowball: "Snowball",
  methodHighbal: "High Balance", methodLowint: "Low Interest",
  methodAvalancheDesc: "Highest rate first", methodSnowballDesc: "Smallest balance first",
  methodHighbalDesc: "Largest balance first", methodLowintDesc: "Lowest rate first",
  resultHeaderTitle: "Your Debt-Free Plan",
  debtFreeIn: "Debt-Free In",
  withExtraMethod: (extra, method) => `with ${extra}/mo extra · ${method} method`,
  chartToday: "Today", chartDebtFree: "Debt Free",
  totalInterest: "Total Interest", totalDebtLabel: "Total Debt", totalPaid: "Total Paid",
  methodComparison: "Method Comparison", interestLabel: "interest",
  editDebts: "Edit My Debts",
  getFullPlan: "Get Your Full 7-Page PDF Plan",
  pdfItem1: "Your debt snapshot — where you stand today",
  pdfItem2: "All 4 methods compared — Avalanche, Snowball, High Balance, Low Interest",
  pdfItem3: "Which debt to attack first — and why",
  pdfItem4: (a, b, c) => `What if you pay 0, ${a}, ${b} or ${c} extra?`,
  pdfItem5: "Month-by-month payment schedule",
  pdfItem6: "Debt consolidation — is it worth it for you?",
  pdfItem7: "Your action plan + FAQ",
  priceBadge: "one-time",
  saves: amt => `Saves ${amt}`,
  downloadPdfBtn: "📄 Download PDF Plan",
  notSureYet: "Not sure yet?", seeWhatsInside: "See what's inside",
  paymentConfirmed: "✓ Payment confirmed",
  readyToDownload: "Your 7-page PDF plan is ready to download.",
  checkDownloads: "Check your downloads for ClearMyDebt-Plan.pdf",
  generatingPdf: "Generating PDF...", downloadAgain: "Download Again", downloadYourPdf: "Download Your PDF",
  paymentUnavailable: "Payment unavailable. Please try again.",
  pdfLoading: "PDF library loading, please wait...",
  sampleTitle: "Sample — Month-by-Month Schedule",
  sampleSubtitle: "Page 3 of 7 · Your full plan has all debts & methods",
  sampleMonth: "Month", samplePayment: "Payment", samplePrincipal: "Principal",
  sampleInterest: "Interest", sampleBalance: "Balance",
  sampleFullPlan: n => `✦ Full plan includes all ${n} debts · 4 payoff methods compared · consolidation analysis`,
  getFullPlanBtn: price => `Get Full 7-Page Plan — ${price}`,
  secureInstant: "Secure · Instant download · No signup required",
  copyright: "© 2026 Weelco Inc. All rights reserved. For informational purposes only. Not financial advice.",
};

const de = {
  headerTitle: "Wann bist du schuldenfrei?",
  headerSubtitle: "Schulden eingeben · Persönlichen Plan in Sekunden erhalten",
  yourDebts: "Deine Schulden",
  colType: "Typ", colBalance: "Betrag", colRate: "Zins %", colMinPay: "Min. Rate",
  placeholderBalance: "Betrag", placeholderRate: "Zins %", placeholderMin: "Min.",
  selectType: "Typ wählen...", addDebt: "+ Schuld hinzufügen",
  required: "Pflichtfeld",
  minPaymentError: min => `Mindestrate muss über ${min}/Monat liegen (monatliche Zinsen)`,
  extraTitle: "Zusätzliche Zahlungsszenarien",
  conservative: "Konservativ", balanced: "Ausgewogen", aggressive: "Aggressiv",
  extraHint: sym => `Schon 50 ${sym} extra/Monat können dir Tausende an Zinsen sparen`,
  totalDebt: "Gesamtschulden",
  calculateBtn: "Meine Tilgung berechnen",
  freeNoSignup: "Kostenlos · Keine Anmeldung · Sofortige Ergebnisse",
  methodAvalanche: "Avalanche", methodSnowball: "Schneeball",
  methodHighbal: "Hoher Betrag", methodLowint: "Niedriger Zins",
  methodAvalancheDesc: "Höchster Zinssatz zuerst", methodSnowballDesc: "Kleinster Betrag zuerst",
  methodHighbalDesc: "Größter Betrag zuerst", methodLowintDesc: "Niedrigster Zinssatz zuerst",
  resultHeaderTitle: "Dein Schuldenfreiheitsplan",
  debtFreeIn: "Schuldenfrei in",
  withExtraMethod: (extra, method) => `mit ${extra}/Monat extra · Methode: ${method}`,
  chartToday: "Heute", chartDebtFree: "Schuldenfrei",
  totalInterest: "Gesamtzinsen", totalDebtLabel: "Gesamtschulden", totalPaid: "Gesamt bezahlt",
  methodComparison: "Methodenvergleich", interestLabel: "Zinsen",
  editDebts: "Schulden bearbeiten",
  getFullPlan: "Hol dir deinen vollständigen 7-seitigen PDF-Plan",
  pdfItem1: "Dein Schulden-Überblick — wo du heute stehst",
  pdfItem2: "Alle 4 Methoden verglichen — Avalanche, Schneeball, Hoher Betrag, Niedriger Zins",
  pdfItem3: "Welche Schuld zuerst angehen — und warum",
  pdfItem4: (a, b, c) => `Was wäre wenn du 0, ${a}, ${b} oder ${c} extra zahlst?`,
  pdfItem5: "Monatlicher Zahlungsplan",
  pdfItem6: "Schuldenkonsolidierung — lohnt es sich für dich?",
  pdfItem7: "Dein Aktionsplan + FAQ",
  priceBadge: "einmalig",
  saves: amt => `Spart ${amt}`,
  downloadPdfBtn: "📄 PDF-Plan herunterladen",
  notSureYet: "Noch unsicher?", seeWhatsInside: "Vorschau ansehen",
  paymentConfirmed: "✓ Zahlung bestätigt",
  readyToDownload: "Dein 7-seitiger PDF-Plan ist bereit zum Herunterladen.",
  checkDownloads: "Sieh in deinen Downloads nach ClearMyDebt-Plan.pdf",
  generatingPdf: "PDF wird erstellt...", downloadAgain: "Erneut herunterladen", downloadYourPdf: "PDF herunterladen",
  paymentUnavailable: "Zahlung nicht verfügbar. Bitte erneut versuchen.",
  pdfLoading: "PDF-Bibliothek wird geladen, bitte warten...",
  sampleTitle: "Beispiel — Monatlicher Zahlungsplan",
  sampleSubtitle: "Seite 3 von 7 · Dein vollständiger Plan enthält alle Schulden und Methoden",
  sampleMonth: "Monat", samplePayment: "Zahlung", samplePrincipal: "Tilgung",
  sampleInterest: "Zinsen", sampleBalance: "Saldo",
  sampleFullPlan: n => `✦ Vollständiger Plan: alle ${n} Schulden · 4 Methoden verglichen · Konsolidierungsanalyse`,
  getFullPlanBtn: price => `Vollständigen 7-Seiten-Plan kaufen — ${price}`,
  secureInstant: "Sicher · Sofortiger Download · Keine Anmeldung",
  copyright: "© 2026 Weelco Inc. Alle Rechte vorbehalten. Nur zu Informationszwecken. Keine Finanzberatung.",
};

const fr = {
  headerTitle: "Quand serez-vous libre de dettes ?",
  headerSubtitle: "Entrez vos dettes · Obtenez un plan personnalisé en quelques secondes",
  yourDebts: "Vos dettes",
  colType: "Type", colBalance: "Montant", colRate: "Taux %", colMinPay: "Min. mensuel",
  placeholderBalance: "Montant", placeholderRate: "Taux %", placeholderMin: "Min.",
  selectType: "Choisir un type...", addDebt: "+ Ajouter une dette",
  required: "Obligatoire",
  minPaymentError: min => `Le paiement minimum doit dépasser ${min}/mois (intérêts mensuels)`,
  extraTitle: "Scénarios de paiement supplémentaire",
  conservative: "Conservateur", balanced: "Équilibré", aggressive: "Agressif",
  extraHint: "Même 50 € de plus par mois peuvent vous faire économiser des milliers en intérêts",
  totalDebt: "Total des dettes",
  calculateBtn: "Calculer ma date de remboursement",
  freeNoSignup: "Gratuit · Sans inscription · Résultats instantanés",
  methodAvalanche: "Avalanche", methodSnowball: "Boule de neige",
  methodHighbal: "Solde élevé", methodLowint: "Faible intérêt",
  methodAvalancheDesc: "Taux le plus élevé en premier", methodSnowballDesc: "Solde le plus faible en premier",
  methodHighbalDesc: "Solde le plus élevé en premier", methodLowintDesc: "Taux le plus faible en premier",
  resultHeaderTitle: "Votre plan de remboursement",
  debtFreeIn: "Sans dette dans",
  withExtraMethod: (extra, method) => `avec ${extra}/mois en plus · méthode ${method}`,
  chartToday: "Aujourd'hui", chartDebtFree: "Sans dette",
  totalInterest: "Total des intérêts", totalDebtLabel: "Total des dettes", totalPaid: "Total payé",
  methodComparison: "Comparaison des méthodes", interestLabel: "intérêts",
  editDebts: "Modifier mes dettes",
  getFullPlan: "Obtenez votre plan PDF complet de 7 pages",
  pdfItem1: "Aperçu de vos dettes — où vous en êtes aujourd'hui",
  pdfItem2: "Les 4 méthodes comparées — Avalanche, Boule de neige, Solde élevé, Faible intérêt",
  pdfItem3: "Quelle dette attaquer en premier — et pourquoi",
  pdfItem4: (a, b, c) => `Et si vous payez 0, ${a}, ${b} ou ${c} de plus ?`,
  pdfItem5: "Calendrier de paiement mois par mois",
  pdfItem6: "Consolidation de dettes — est-ce rentable pour vous ?",
  pdfItem7: "Votre plan d'action + FAQ",
  priceBadge: "paiement unique",
  saves: amt => `Économise ${amt}`,
  downloadPdfBtn: "📄 Télécharger le plan PDF",
  notSureYet: "Pas encore sûr ?", seeWhatsInside: "Voir le contenu",
  paymentConfirmed: "✓ Paiement confirmé",
  readyToDownload: "Votre plan PDF de 7 pages est prêt à télécharger.",
  checkDownloads: "Vérifiez vos téléchargements pour ClearMyDebt-Plan.pdf",
  generatingPdf: "Génération du PDF...", downloadAgain: "Télécharger à nouveau", downloadYourPdf: "Télécharger votre PDF",
  paymentUnavailable: "Paiement indisponible. Veuillez réessayer.",
  pdfLoading: "Chargement de la bibliothèque PDF, veuillez patienter...",
  sampleTitle: "Exemple — Calendrier mensuel",
  sampleSubtitle: "Page 3 sur 7 · Votre plan complet inclut toutes les dettes et méthodes",
  sampleMonth: "Mois", samplePayment: "Paiement", samplePrincipal: "Capital",
  sampleInterest: "Intérêts", sampleBalance: "Solde",
  sampleFullPlan: n => `✦ Plan complet : ${n} dettes · 4 méthodes comparées · analyse de consolidation`,
  getFullPlanBtn: price => `Plan complet 7 pages — ${price}`,
  secureInstant: "Sécurisé · Téléchargement instantané · Sans inscription",
  copyright: "© 2026 Weelco Inc. Tous droits réservés. À titre informatif uniquement. Pas de conseil financier.",
};

const es = {
  headerTitle: "¿Cuándo estarás libre de deudas?",
  headerSubtitle: "Ingresa tus deudas · Obtén un plan personalizado en segundos",
  yourDebts: "Tus deudas",
  colType: "Tipo", colBalance: "Saldo", colRate: "Tasa %", colMinPay: "Pago mín.",
  placeholderBalance: "Saldo", placeholderRate: "Tasa %", placeholderMin: "Mín.",
  selectType: "Seleccionar tipo...", addDebt: "+ Agregar deuda",
  required: "Obligatorio",
  minPaymentError: min => `El pago mínimo debe ser mayor que ${min}/mes (interés mensual)`,
  extraTitle: "Escenarios de pago adicional",
  conservative: "Conservador", balanced: "Equilibrado", aggressive: "Agresivo",
  extraHint: sym => `Incluso ${sym}50 extra al mes pueden ahorrarte miles en intereses`,
  totalDebt: "Total de deudas",
  calculateBtn: "Calcular mi fecha de pago",
  freeNoSignup: "Gratis · Sin registro · Resultados instantáneos",
  methodAvalanche: "Avalancha", methodSnowball: "Bola de nieve",
  methodHighbal: "Saldo alto", methodLowint: "Interés bajo",
  methodAvalancheDesc: "Mayor tasa primero", methodSnowballDesc: "Menor saldo primero",
  methodHighbalDesc: "Mayor saldo primero", methodLowintDesc: "Menor tasa primero",
  resultHeaderTitle: "Tu plan para salir de deudas",
  debtFreeIn: "Libre de deudas en",
  withExtraMethod: (extra, method) => `con ${extra}/mes extra · método ${method}`,
  chartToday: "Hoy", chartDebtFree: "Sin deudas",
  totalInterest: "Total de intereses", totalDebtLabel: "Total de deudas", totalPaid: "Total pagado",
  methodComparison: "Comparación de métodos", interestLabel: "interés",
  editDebts: "Editar mis deudas",
  getFullPlan: "Obtén tu plan PDF completo de 7 páginas",
  pdfItem1: "Resumen de tus deudas — dónde estás hoy",
  pdfItem2: "Los 4 métodos comparados — Avalancha, Bola de nieve, Saldo alto, Interés bajo",
  pdfItem3: "Qué deuda atacar primero — y por qué",
  pdfItem4: (a, b, c) => `¿Qué pasaría si pagas 0, ${a}, ${b} o ${c} extra?`,
  pdfItem5: "Calendario de pagos mes a mes",
  pdfItem6: "Consolidación de deudas — ¿vale la pena para ti?",
  pdfItem7: "Tu plan de acción + preguntas frecuentes",
  priceBadge: "pago único",
  saves: amt => `Ahorra ${amt}`,
  downloadPdfBtn: "📄 Descargar plan PDF",
  notSureYet: "¿Aún no estás seguro?", seeWhatsInside: "Ver el contenido",
  paymentConfirmed: "✓ Pago confirmado",
  readyToDownload: "Tu plan PDF de 7 páginas está listo para descargar.",
  checkDownloads: "Busca ClearMyDebt-Plan.pdf en tus descargas",
  generatingPdf: "Generando PDF...", downloadAgain: "Descargar de nuevo", downloadYourPdf: "Descargar tu PDF",
  paymentUnavailable: "Pago no disponible. Por favor intenta de nuevo.",
  pdfLoading: "Cargando biblioteca PDF, por favor espera...",
  sampleTitle: "Ejemplo — Calendario mensual de pagos",
  sampleSubtitle: "Página 3 de 7 · Tu plan completo incluye todas las deudas y métodos",
  sampleMonth: "Mes", samplePayment: "Pago", samplePrincipal: "Capital",
  sampleInterest: "Interés", sampleBalance: "Saldo",
  sampleFullPlan: n => `✦ Plan completo: ${n} deudas · 4 métodos comparados · análisis de consolidación`,
  getFullPlanBtn: price => `Plan completo de 7 páginas — ${price}`,
  secureInstant: "Seguro · Descarga instantánea · Sin registro",
  copyright: "© 2026 Weelco Inc. Todos los derechos reservados. Solo con fines informativos. No es asesoramiento financiero.",
};

const pt = {
  headerTitle: "Quando você ficará livre de dívidas?",
  headerSubtitle: "Insira suas dívidas · Obtenha um plano personalizado em segundos",
  yourDebts: "Suas dívidas",
  colType: "Tipo", colBalance: "Saldo", colRate: "Taxa %", colMinPay: "Pgto. mín.",
  placeholderBalance: "Saldo", placeholderRate: "Taxa %", placeholderMin: "Mín.",
  selectType: "Selecionar tipo...", addDebt: "+ Adicionar dívida",
  required: "Obrigatório",
  minPaymentError: min => `O pagamento mínimo deve ser maior que ${min}/mês (juros mensais)`,
  extraTitle: "Cenários de pagamento extra",
  conservative: "Conservador", balanced: "Equilibrado", aggressive: "Agressivo",
  extraHint: sym => `Mesmo ${sym}50 extra por mês podem economizar milhares em juros`,
  totalDebt: "Total de dívidas",
  calculateBtn: "Calcular minha data de quitação",
  freeNoSignup: "Grátis · Sem cadastro · Resultados instantâneos",
  methodAvalanche: "Avalanche", methodSnowball: "Bola de neve",
  methodHighbal: "Maior saldo", methodLowint: "Menor juros",
  methodAvalancheDesc: "Maior taxa primeiro", methodSnowballDesc: "Menor saldo primeiro",
  methodHighbalDesc: "Maior saldo primeiro", methodLowintDesc: "Menor taxa primeiro",
  resultHeaderTitle: "Seu plano para quitar dívidas",
  debtFreeIn: "Livre de dívidas em",
  withExtraMethod: (extra, method) => `com ${extra}/mês extra · método ${method}`,
  chartToday: "Hoje", chartDebtFree: "Sem dívidas",
  totalInterest: "Total de juros", totalDebtLabel: "Total de dívidas", totalPaid: "Total pago",
  methodComparison: "Comparação de métodos", interestLabel: "juros",
  editDebts: "Editar minhas dívidas",
  getFullPlan: "Obtenha seu plano PDF completo de 7 páginas",
  pdfItem1: "Visão geral das suas dívidas — onde você está hoje",
  pdfItem2: "Os 4 métodos comparados — Avalanche, Bola de neve, Maior saldo, Menor juros",
  pdfItem3: "Qual dívida atacar primeiro — e por quê",
  pdfItem4: (a, b, c) => `E se você pagar 0, ${a}, ${b} ou ${c} a mais?`,
  pdfItem5: "Cronograma de pagamento mês a mês",
  pdfItem6: "Consolidação de dívidas — vale a pena para você?",
  pdfItem7: "Seu plano de ação + perguntas frequentes",
  priceBadge: "pagamento único",
  saves: amt => `Economiza ${amt}`,
  downloadPdfBtn: "📄 Baixar plano PDF",
  notSureYet: "Ainda não tem certeza?", seeWhatsInside: "Ver o conteúdo",
  paymentConfirmed: "✓ Pagamento confirmado",
  readyToDownload: "Seu plano PDF de 7 páginas está pronto para download.",
  checkDownloads: "Verifique seus downloads por ClearMyDebt-Plan.pdf",
  generatingPdf: "Gerando PDF...", downloadAgain: "Baixar novamente", downloadYourPdf: "Baixar seu PDF",
  paymentUnavailable: "Pagamento indisponível. Por favor, tente novamente.",
  pdfLoading: "Carregando biblioteca PDF, por favor aguarde...",
  sampleTitle: "Exemplo — Cronograma mensal",
  sampleSubtitle: "Página 3 de 7 · Seu plano completo inclui todas as dívidas e métodos",
  sampleMonth: "Mês", samplePayment: "Pagamento", samplePrincipal: "Principal",
  sampleInterest: "Juros", sampleBalance: "Saldo",
  sampleFullPlan: n => `✦ Plano completo: ${n} dívidas · 4 métodos comparados · análise de consolidação`,
  getFullPlanBtn: price => `Plano completo de 7 páginas — ${price}`,
  secureInstant: "Seguro · Download instantâneo · Sem cadastro",
  copyright: "© 2026 Weelco Inc. Todos os direitos reservados. Apenas para fins informativos. Não é aconselhamento financeiro.",
};

const it = {
  headerTitle: "Quando sarai libero dai debiti?",
  headerSubtitle: "Inserisci i tuoi debiti · Ottieni un piano personalizzato in pochi secondi",
  yourDebts: "I tuoi debiti",
  colType: "Tipo", colBalance: "Saldo", colRate: "Tasso %", colMinPay: "Rata min.",
  placeholderBalance: "Saldo", placeholderRate: "Tasso %", placeholderMin: "Min.",
  selectType: "Seleziona tipo...", addDebt: "+ Aggiungi debito",
  required: "Obbligatorio",
  minPaymentError: min => `La rata minima deve essere superiore a ${min}/mese (interessi mensili)`,
  extraTitle: "Scenari di pagamento aggiuntivo",
  conservative: "Conservativo", balanced: "Bilanciato", aggressive: "Aggressivo",
  extraHint: "Anche 50 € in più al mese possono farti risparmiare migliaia di euro in interessi",
  totalDebt: "Totale debiti",
  calculateBtn: "Calcola la mia data di estinzione",
  freeNoSignup: "Gratis · Senza registrazione · Risultati immediati",
  methodAvalanche: "Valanga", methodSnowball: "Palla di neve",
  methodHighbal: "Saldo alto", methodLowint: "Interesse basso",
  methodAvalancheDesc: "Tasso più alto prima", methodSnowballDesc: "Saldo più basso prima",
  methodHighbalDesc: "Saldo più alto prima", methodLowintDesc: "Tasso più basso prima",
  resultHeaderTitle: "Il tuo piano per liberarti dai debiti",
  debtFreeIn: "Libero dai debiti in",
  withExtraMethod: (extra, method) => `con ${extra}/mese extra · metodo ${method}`,
  chartToday: "Oggi", chartDebtFree: "Senza debiti",
  totalInterest: "Totale interessi", totalDebtLabel: "Totale debiti", totalPaid: "Totale pagato",
  methodComparison: "Confronto metodi", interestLabel: "interessi",
  editDebts: "Modifica i miei debiti",
  getFullPlan: "Ottieni il tuo piano PDF completo di 7 pagine",
  pdfItem1: "Panoramica dei tuoi debiti — dove sei oggi",
  pdfItem2: "Tutti e 4 i metodi a confronto — Valanga, Palla di neve, Saldo alto, Interesse basso",
  pdfItem3: "Quale debito affrontare per primo — e perché",
  pdfItem4: (a, b, c) => `Cosa succederebbe se pagassi 0, ${a}, ${b} o ${c} in più?`,
  pdfItem5: "Calendario dei pagamenti mese per mese",
  pdfItem6: "Consolidamento dei debiti — vale la pena per te?",
  pdfItem7: "Il tuo piano d'azione + FAQ",
  priceBadge: "pagamento unico",
  saves: amt => `Risparmia ${amt}`,
  downloadPdfBtn: "📄 Scarica il piano PDF",
  notSureYet: "Non sei ancora sicuro?", seeWhatsInside: "Vedi il contenuto",
  paymentConfirmed: "✓ Pagamento confermato",
  readyToDownload: "Il tuo piano PDF di 7 pagine è pronto per il download.",
  checkDownloads: "Controlla i tuoi download per ClearMyDebt-Plan.pdf",
  generatingPdf: "Generazione PDF...", downloadAgain: "Scarica di nuovo", downloadYourPdf: "Scarica il tuo PDF",
  paymentUnavailable: "Pagamento non disponibile. Per favore riprova.",
  pdfLoading: "Caricamento libreria PDF, per favore attendi...",
  sampleTitle: "Esempio — Calendario mensile",
  sampleSubtitle: "Pagina 3 di 7 · Il tuo piano completo include tutti i debiti e i metodi",
  sampleMonth: "Mese", samplePayment: "Pagamento", samplePrincipal: "Capitale",
  sampleInterest: "Interessi", sampleBalance: "Saldo",
  sampleFullPlan: n => `✦ Piano completo: ${n} debiti · 4 metodi a confronto · analisi di consolidamento`,
  getFullPlanBtn: price => `Piano completo di 7 pagine — ${price}`,
  secureInstant: "Sicuro · Download immediato · Senza registrazione",
  copyright: "© 2026 Weelco Inc. Tutti i diritti riservati. Solo a scopo informativo. Non è una consulenza finanziaria.",
};

const nl = {
  headerTitle: "Wanneer ben je schuldenvrij?",
  headerSubtitle: "Voer je schulden in · Ontvang een persoonlijk plan in seconden",
  yourDebts: "Jouw schulden",
  colType: "Type", colBalance: "Bedrag", colRate: "Rente %", colMinPay: "Min. betaling",
  placeholderBalance: "Bedrag", placeholderRate: "Rente %", placeholderMin: "Min.",
  selectType: "Kies type...", addDebt: "+ Schuld toevoegen",
  required: "Verplicht",
  minPaymentError: min => `Minimumbetaling moet meer dan ${min}/maand zijn (maandelijkse rente)`,
  extraTitle: "Extra betalingsscenario's",
  conservative: "Conservatief", balanced: "Gebalanceerd", aggressive: "Agressief",
  extraHint: "Zelfs €50 extra per maand kan je duizenden aan rente besparen",
  totalDebt: "Totale schuld",
  calculateBtn: "Bereken mijn aflossingsdatum",
  freeNoSignup: "Gratis · Geen aanmelding · Directe resultaten",
  methodAvalanche: "Avalanche", methodSnowball: "Sneeuwbal",
  methodHighbal: "Hoog saldo", methodLowint: "Lage rente",
  methodAvalancheDesc: "Hoogste rente eerst", methodSnowballDesc: "Laagste saldo eerst",
  methodHighbalDesc: "Hoogste saldo eerst", methodLowintDesc: "Laagste rente eerst",
  resultHeaderTitle: "Jouw schuldenvrij plan",
  debtFreeIn: "Schuldenvrij in",
  withExtraMethod: (extra, method) => `met ${extra}/maand extra · methode ${method}`,
  chartToday: "Vandaag", chartDebtFree: "Schuldenvrij",
  totalInterest: "Totale rente", totalDebtLabel: "Totale schuld", totalPaid: "Totaal betaald",
  methodComparison: "Methodevergelijking", interestLabel: "rente",
  editDebts: "Schulden bewerken",
  getFullPlan: "Ontvang je volledige 7-pagina PDF-plan",
  pdfItem1: "Overzicht van je schulden — waar je vandaag staat",
  pdfItem2: "Alle 4 methoden vergeleken — Avalanche, Sneeuwbal, Hoog saldo, Lage rente",
  pdfItem3: "Welke schuld eerst aanpakken — en waarom",
  pdfItem4: (a, b, c) => `Wat als je 0, ${a}, ${b} of ${c} extra betaalt?`,
  pdfItem5: "Maandelijkse betalingsplanning",
  pdfItem6: "Schuldenconsolidatie — is het de moeite waard voor jou?",
  pdfItem7: "Jouw actieplan + FAQ",
  priceBadge: "eenmalig",
  saves: amt => `Bespaart ${amt}`,
  downloadPdfBtn: "📄 PDF-plan downloaden",
  notSureYet: "Nog niet zeker?", seeWhatsInside: "Bekijk de inhoud",
  paymentConfirmed: "✓ Betaling bevestigd",
  readyToDownload: "Je 7-pagina PDF-plan is klaar om te downloaden.",
  checkDownloads: "Controleer je downloads voor ClearMyDebt-Plan.pdf",
  generatingPdf: "PDF wordt gegenereerd...", downloadAgain: "Opnieuw downloaden", downloadYourPdf: "PDF downloaden",
  paymentUnavailable: "Betaling niet beschikbaar. Probeer het opnieuw.",
  pdfLoading: "PDF-bibliotheek laden, even geduld...",
  sampleTitle: "Voorbeeld — Maandelijks betalingsoverzicht",
  sampleSubtitle: "Pagina 3 van 7 · Je volledige plan bevat alle schulden en methoden",
  sampleMonth: "Maand", samplePayment: "Betaling", samplePrincipal: "Aflossing",
  sampleInterest: "Rente", sampleBalance: "Saldo",
  sampleFullPlan: n => `✦ Volledig plan: ${n} schulden · 4 methoden vergeleken · consolidatieanalyse`,
  getFullPlanBtn: price => `Volledig 7-paginaplan — ${price}`,
  secureInstant: "Veilig · Direct download · Geen aanmelding",
  copyright: "© 2026 Weelco Inc. Alle rechten voorbehouden. Alleen voor informatieve doeleinden. Geen financieel advies.",
};

const sv = {
  headerTitle: "När blir du skuldfri?",
  headerSubtitle: "Ange dina skulder · Få en personlig plan på sekunder",
  yourDebts: "Dina skulder",
  colType: "Typ", colBalance: "Belopp", colRate: "Ränta %", colMinPay: "Min. betalning",
  placeholderBalance: "Belopp", placeholderRate: "Ränta %", placeholderMin: "Min.",
  selectType: "Välj typ...", addDebt: "+ Lägg till skuld",
  required: "Obligatoriskt",
  minPaymentError: min => `Minsta betalning måste vara mer än ${min}/mån (månadsränta)`,
  extraTitle: "Extrabetalningsscenarier",
  conservative: "Konservativ", balanced: "Balanserad", aggressive: "Aggressiv",
  extraHint: "Redan 500 kr extra per månad kan spara dig tusentals kronor i ränta",
  totalDebt: "Total skuld",
  calculateBtn: "Beräkna mitt amorteringsdatum",
  freeNoSignup: "Gratis · Ingen registrering · Omedelbara resultat",
  methodAvalanche: "Avalanche", methodSnowball: "Snöboll",
  methodHighbal: "Högt saldo", methodLowint: "Låg ränta",
  methodAvalancheDesc: "Högsta ränta först", methodSnowballDesc: "Lägsta saldo först",
  methodHighbalDesc: "Högsta saldo först", methodLowintDesc: "Lägsta ränta först",
  resultHeaderTitle: "Din skuldfriplan",
  debtFreeIn: "Skuldfri om",
  withExtraMethod: (extra, method) => `med ${extra}/mån extra · metod ${method}`,
  chartToday: "Idag", chartDebtFree: "Skuldfri",
  totalInterest: "Total ränta", totalDebtLabel: "Total skuld", totalPaid: "Totalt betalt",
  methodComparison: "Metodjämförelse", interestLabel: "ränta",
  editDebts: "Redigera skulder",
  getFullPlan: "Få din kompletta 7-sidig PDF-plan",
  pdfItem1: "Din skuldöversikt — var du står idag",
  pdfItem2: "Alla 4 metoder jämförda — Avalanche, Snöboll, Högt saldo, Låg ränta",
  pdfItem3: "Vilken skuld att attackera först — och varför",
  pdfItem4: (a, b, c) => `Vad händer om du betalar 0, ${a}, ${b} eller ${c} extra?`,
  pdfItem5: "Månadsvis betalningsschema",
  pdfItem6: "Skuldkonsolidering — är det värt det för dig?",
  pdfItem7: "Din handlingsplan + FAQ",
  priceBadge: "engångsbetalning",
  saves: amt => `Sparar ${amt}`,
  downloadPdfBtn: "📄 Ladda ner PDF-plan",
  notSureYet: "Inte säker ännu?", seeWhatsInside: "Se vad som ingår",
  paymentConfirmed: "✓ Betalning bekräftad",
  readyToDownload: "Din 7-sidiga PDF-plan är redo att laddas ner.",
  checkDownloads: "Kontrollera dina nedladdningar för ClearMyDebt-Plan.pdf",
  generatingPdf: "Genererar PDF...", downloadAgain: "Ladda ner igen", downloadYourPdf: "Ladda ner din PDF",
  paymentUnavailable: "Betalning ej tillgänglig. Försök igen.",
  pdfLoading: "Laddar PDF-bibliotek, vänta...",
  sampleTitle: "Exempel — Månadsvis schema",
  sampleSubtitle: "Sida 3 av 7 · Din fullständiga plan inkluderar alla skulder och metoder",
  sampleMonth: "Månad", samplePayment: "Betalning", samplePrincipal: "Amortering",
  sampleInterest: "Ränta", sampleBalance: "Saldo",
  sampleFullPlan: n => `✦ Komplett plan: ${n} skulder · 4 metoder jämförda · konsolideringsanalys`,
  getFullPlanBtn: price => `Komplett 7-sidig plan — ${price}`,
  secureInstant: "Säkert · Omedelbar nedladdning · Ingen registrering",
  copyright: "© 2026 Weelco Inc. Alla rättigheter förbehållna. Endast i informationssyfte. Inte finansiell rådgivning.",
};

const nb = {
  headerTitle: "Når blir du gjeldfri?",
  headerSubtitle: "Skriv inn gjelden din · Få en personlig plan på sekunder",
  yourDebts: "Din gjeld",
  colType: "Type", colBalance: "Beløp", colRate: "Rente %", colMinPay: "Min. betaling",
  placeholderBalance: "Beløp", placeholderRate: "Rente %", placeholderMin: "Min.",
  selectType: "Velg type...", addDebt: "+ Legg til gjeld",
  required: "Obligatorisk",
  minPaymentError: min => `Minste betaling må være mer enn ${min}/mnd (månedlig rente)`,
  extraTitle: "Ekstrabetalingsscenarier",
  conservative: "Konservativ", balanced: "Balansert", aggressive: "Aggressiv",
  extraHint: "Selv 500 kr ekstra per måned kan spare deg for tusenvis i renter",
  totalDebt: "Total gjeld",
  calculateBtn: "Beregn min nedbetalingsdato",
  freeNoSignup: "Gratis · Ingen registrering · Øyeblikkelige resultater",
  methodAvalanche: "Avalanche", methodSnowball: "Snøball",
  methodHighbal: "Høy saldo", methodLowint: "Lav rente",
  methodAvalancheDesc: "Høyeste rente først", methodSnowballDesc: "Laveste saldo først",
  methodHighbalDesc: "Høyeste saldo først", methodLowintDesc: "Laveste rente først",
  resultHeaderTitle: "Din gjeldfriplan",
  debtFreeIn: "Gjeldfri om",
  withExtraMethod: (extra, method) => `med ${extra}/mnd ekstra · metode ${method}`,
  chartToday: "I dag", chartDebtFree: "Gjeldfri",
  totalInterest: "Total rente", totalDebtLabel: "Total gjeld", totalPaid: "Totalt betalt",
  methodComparison: "Metodsammenligning", interestLabel: "rente",
  editDebts: "Rediger gjeld",
  getFullPlan: "Få din komplette 7-siders PDF-plan",
  pdfItem1: "Din gjeldsoppsummering — hvor du står i dag",
  pdfItem2: "Alle 4 metoder sammenlignet — Avalanche, Snøball, Høy saldo, Lav rente",
  pdfItem3: "Hvilken gjeld å angripe først — og hvorfor",
  pdfItem4: (a, b, c) => `Hva om du betaler 0, ${a}, ${b} eller ${c} ekstra?`,
  pdfItem5: "Månedlig betalingsplan",
  pdfItem6: "Gjeldskonsolidering — er det verdt det for deg?",
  pdfItem7: "Din handlingsplan + FAQ",
  priceBadge: "engangsbeløp",
  saves: amt => `Sparer ${amt}`,
  downloadPdfBtn: "📄 Last ned PDF-plan",
  notSureYet: "Ikke sikker ennå?", seeWhatsInside: "Se hva som er inni",
  paymentConfirmed: "✓ Betaling bekreftet",
  readyToDownload: "Din 7-siders PDF-plan er klar til nedlasting.",
  checkDownloads: "Sjekk nedlastingene dine for ClearMyDebt-Plan.pdf",
  generatingPdf: "Genererer PDF...", downloadAgain: "Last ned igjen", downloadYourPdf: "Last ned din PDF",
  paymentUnavailable: "Betaling utilgjengelig. Prøv igjen.",
  pdfLoading: "Laster PDF-bibliotek, vennligst vent...",
  sampleTitle: "Eksempel — Månedlig betalingsplan",
  sampleSubtitle: "Side 3 av 7 · Din fullstendige plan inkluderer all gjeld og alle metoder",
  sampleMonth: "Måned", samplePayment: "Betaling", samplePrincipal: "Avdrag",
  sampleInterest: "Rente", sampleBalance: "Saldo",
  sampleFullPlan: n => `✦ Komplett plan: ${n} gjeldsposter · 4 metoder sammenlignet · konsolideringsanalyse`,
  getFullPlanBtn: price => `Komplett 7-siders plan — ${price}`,
  secureInstant: "Sikkert · Øyeblikkelig nedlasting · Ingen registrering",
  copyright: "© 2026 Weelco Inc. Alle rettigheter forbeholdt. Kun til informasjonsformål. Ikke finansiell rådgivning.",
};

const da = {
  headerTitle: "Hvornår bliver du gældfri?",
  headerSubtitle: "Indtast din gæld · Få en personlig plan på sekunder",
  yourDebts: "Din gæld",
  colType: "Type", colBalance: "Beløb", colRate: "Rente %", colMinPay: "Min. betaling",
  placeholderBalance: "Beløb", placeholderRate: "Rente %", placeholderMin: "Min.",
  selectType: "Vælg type...", addDebt: "+ Tilføj gæld",
  required: "Obligatorisk",
  minPaymentError: min => `Minimumsbetaling skal være mere end ${min}/mdr. (månedlig rente)`,
  extraTitle: "Ekstra betalingsscenarier",
  conservative: "Konservativ", balanced: "Afbalanceret", aggressive: "Aggressiv",
  extraHint: "Selv 500 kr. ekstra om måneden kan spare dig for tusindvis i renter",
  totalDebt: "Samlet gæld",
  calculateBtn: "Beregn min afdragsdato",
  freeNoSignup: "Gratis · Ingen tilmelding · Øjeblikkelige resultater",
  methodAvalanche: "Avalanche", methodSnowball: "Snebold",
  methodHighbal: "Høj saldo", methodLowint: "Lav rente",
  methodAvalancheDesc: "Højeste rente først", methodSnowballDesc: "Laveste saldo først",
  methodHighbalDesc: "Højeste saldo først", methodLowintDesc: "Laveste rente først",
  resultHeaderTitle: "Din gældfriplan",
  debtFreeIn: "Gældfri om",
  withExtraMethod: (extra, method) => `med ${extra}/mdr. ekstra · metode ${method}`,
  chartToday: "I dag", chartDebtFree: "Gældfri",
  totalInterest: "Samlet rente", totalDebtLabel: "Samlet gæld", totalPaid: "Samlet betalt",
  methodComparison: "Metodesammenligning", interestLabel: "rente",
  editDebts: "Rediger gæld",
  getFullPlan: "Få din komplette 7-siders PDF-plan",
  pdfItem1: "Dit gældsoverblik — hvor du står i dag",
  pdfItem2: "Alle 4 metoder sammenlignet — Avalanche, Snebold, Høj saldo, Lav rente",
  pdfItem3: "Hvilken gæld du skal angribe først — og hvorfor",
  pdfItem4: (a, b, c) => `Hvad hvis du betaler 0, ${a}, ${b} eller ${c} ekstra?`,
  pdfItem5: "Månedlig betalingsplan",
  pdfItem6: "Gældskonsolidering — er det det værd for dig?",
  pdfItem7: "Din handlingsplan + FAQ",
  priceBadge: "engangsbeløb",
  saves: amt => `Sparer ${amt}`,
  downloadPdfBtn: "📄 Download PDF-plan",
  notSureYet: "Ikke sikker endnu?", seeWhatsInside: "Se hvad der er indeni",
  paymentConfirmed: "✓ Betaling bekræftet",
  readyToDownload: "Din 7-siders PDF-plan er klar til download.",
  checkDownloads: "Tjek dine downloads for ClearMyDebt-Plan.pdf",
  generatingPdf: "Genererer PDF...", downloadAgain: "Download igen", downloadYourPdf: "Download din PDF",
  paymentUnavailable: "Betaling utilgængelig. Prøv igen.",
  pdfLoading: "Indlæser PDF-bibliotek, vent venligst...",
  sampleTitle: "Eksempel — Månedlig betalingsplan",
  sampleSubtitle: "Side 3 af 7 · Din komplette plan inkluderer al gæld og alle metoder",
  sampleMonth: "Måned", samplePayment: "Betaling", samplePrincipal: "Afdrag",
  sampleInterest: "Rente", sampleBalance: "Saldo",
  sampleFullPlan: n => `✦ Komplet plan: ${n} gældsposter · 4 metoder sammenlignet · konsolideringsanalyse`,
  getFullPlanBtn: price => `Komplet 7-siders plan — ${price}`,
  secureInstant: "Sikkert · Øjeblikkelig download · Ingen tilmelding",
  copyright: "© 2026 Weelco Inc. Alle rettigheder forbeholdt. Kun til informationsformål. Ikke finansiel rådgivning.",
};

const fi = {
  headerTitle: "Milloin olet velaton?",
  headerSubtitle: "Syötä velkasi · Saa henkilökohtainen suunnitelma sekunneissa",
  yourDebts: "Velkasi",
  colType: "Tyyppi", colBalance: "Saldo", colRate: "Korko %", colMinPay: "Min. maksu",
  placeholderBalance: "Saldo", placeholderRate: "Korko %", placeholderMin: "Min.",
  selectType: "Valitse tyyppi...", addDebt: "+ Lisää velka",
  required: "Pakollinen",
  minPaymentError: min => `Vähimmäismaksun on oltava yli ${min}/kk (kuukausikorko)`,
  extraTitle: "Lisämaksuskenaariot",
  conservative: "Konservatiivinen", balanced: "Tasapainoinen", aggressive: "Aggressiivinen",
  extraHint: "Jo 50 € lisää kuukaudessa voi säästää tuhansia euroissa koroissa",
  totalDebt: "Velat yhteensä",
  calculateBtn: "Laske takaisinmaksupäiväni",
  freeNoSignup: "Ilmainen · Ei rekisteröintiä · Välittömät tulokset",
  methodAvalanche: "Avalanche", methodSnowball: "Lumipallo",
  methodHighbal: "Korkea saldo", methodLowint: "Matala korko",
  methodAvalancheDesc: "Korkein korko ensin", methodSnowballDesc: "Pienin saldo ensin",
  methodHighbalDesc: "Suurin saldo ensin", methodLowintDesc: "Matalin korko ensin",
  resultHeaderTitle: "Velattomuussuunnitelmasi",
  debtFreeIn: "Velaton",
  withExtraMethod: (extra, method) => `${extra}/kk lisää · ${method}-menetelmä`,
  chartToday: "Tänään", chartDebtFree: "Velaton",
  totalInterest: "Korot yhteensä", totalDebtLabel: "Velat yhteensä", totalPaid: "Maksettu yhteensä",
  methodComparison: "Menetelmävertailu", interestLabel: "korot",
  editDebts: "Muokkaa velkoja",
  getFullPlan: "Hanki täydellinen 7-sivuinen PDF-suunnitelmasi",
  pdfItem1: "Velkayhteenvetosi — missä olet tänään",
  pdfItem2: "Kaikki 4 menetelmää vertailtu — Avalanche, Lumipallo, Korkea saldo, Matala korko",
  pdfItem3: "Mitä velkaa kannattaa hyökätä ensin — ja miksi",
  pdfItem4: (a, b, c) => `Mitä jos maksat 0, ${a}, ${b} tai ${c} lisää?`,
  pdfItem5: "Kuukausittainen maksuaikataulu",
  pdfItem6: "Velkakonsolidointi — onko se sinulle kannattavaa?",
  pdfItem7: "Toimintasuunnitelmasi + FAQ",
  priceBadge: "kertamaksu",
  saves: amt => `Säästää ${amt}`,
  downloadPdfBtn: "📄 Lataa PDF-suunnitelma",
  notSureYet: "Etkö ole vielä varma?", seeWhatsInside: "Katso mitä sisältää",
  paymentConfirmed: "✓ Maksu vahvistettu",
  readyToDownload: "7-sivuinen PDF-suunnitelmasi on valmis ladattavaksi.",
  checkDownloads: "Tarkista latauksesi ClearMyDebt-Plan.pdf",
  generatingPdf: "Luodaan PDF...", downloadAgain: "Lataa uudelleen", downloadYourPdf: "Lataa PDF",
  paymentUnavailable: "Maksu ei käytettävissä. Yritä uudelleen.",
  pdfLoading: "Ladataan PDF-kirjastoa, odota...",
  sampleTitle: "Esimerkki — Kuukausittainen maksuaikataulu",
  sampleSubtitle: "Sivu 3/7 · Täydellinen suunnitelmasi sisältää kaikki velat ja menetelmät",
  sampleMonth: "Kuukausi", samplePayment: "Maksu", samplePrincipal: "Pääoma",
  sampleInterest: "Korko", sampleBalance: "Saldo",
  sampleFullPlan: n => `✦ Täydellinen suunnitelma: ${n} velkaa · 4 menetelmää vertailtu · konsolidointianalyysi`,
  getFullPlanBtn: price => `Täydellinen 7-sivuinen suunnitelma — ${price}`,
  secureInstant: "Turvallinen · Välitön lataus · Ei rekisteröintiä",
  copyright: "© 2026 Weelco Inc. Kaikki oikeudet pidätetään. Vain tiedoksi. Ei taloudellista neuvontaa.",
};

const pl = {
  headerTitle: "Kiedy będziesz wolny od długów?",
  headerSubtitle: "Wprowadź swoje długi · Otrzymaj spersonalizowany plan w sekundy",
  yourDebts: "Twoje długi",
  colType: "Typ", colBalance: "Saldo", colRate: "Oprocentowanie %", colMinPay: "Min. rata",
  placeholderBalance: "Saldo", placeholderRate: "Oprocentowanie %", placeholderMin: "Min.",
  selectType: "Wybierz typ...", addDebt: "+ Dodaj dług",
  required: "Wymagane",
  minPaymentError: min => `Minimalna rata musi być większa niż ${min}/mies. (miesięczne odsetki)`,
  extraTitle: "Scenariusze dodatkowych wpłat",
  conservative: "Konserwatywny", balanced: "Zrównoważony", aggressive: "Agresywny",
  extraHint: "Nawet 200 zł extra miesięcznie może zaoszczędzić tysiące na odsetkach",
  totalDebt: "Łączny dług",
  calculateBtn: "Oblicz datę spłaty",
  freeNoSignup: "Bezpłatnie · Bez rejestracji · Natychmiastowe wyniki",
  methodAvalanche: "Avalanche", methodSnowball: "Kula śniegowa",
  methodHighbal: "Wysokie saldo", methodLowint: "Niskie oprocentowanie",
  methodAvalancheDesc: "Najwyższe oprocentowanie najpierw", methodSnowballDesc: "Najniższe saldo najpierw",
  methodHighbalDesc: "Najwyższe saldo najpierw", methodLowintDesc: "Najniższe oprocentowanie najpierw",
  resultHeaderTitle: "Twój plan wyjścia z długów",
  debtFreeIn: "Wolny od długów za",
  withExtraMethod: (extra, method) => `z ${extra}/mies. extra · metoda ${method}`,
  chartToday: "Dziś", chartDebtFree: "Bez długów",
  totalInterest: "Łączne odsetki", totalDebtLabel: "Łączny dług", totalPaid: "Łącznie zapłacono",
  methodComparison: "Porównanie metod", interestLabel: "odsetki",
  editDebts: "Edytuj długi",
  getFullPlan: "Pobierz swój kompletny 7-stronicowy plan PDF",
  pdfItem1: "Przegląd twoich długów — gdzie jesteś dziś",
  pdfItem2: "Wszystkie 4 metody porównane — Avalanche, Kula śniegowa, Wysokie saldo, Niskie oprocentowanie",
  pdfItem3: "Który dług zaatakować pierwszy — i dlaczego",
  pdfItem4: (a, b, c) => `Co jeśli zapłacisz 0, ${a}, ${b} lub ${c} extra?`,
  pdfItem5: "Miesięczny harmonogram płatności",
  pdfItem6: "Konsolidacja długów — czy warto dla ciebie?",
  pdfItem7: "Twój plan działania + FAQ",
  priceBadge: "jednorazowo",
  saves: amt => `Oszczędza ${amt}`,
  downloadPdfBtn: "📄 Pobierz plan PDF",
  notSureYet: "Jeszcze niepewny?", seeWhatsInside: "Zobacz co w środku",
  paymentConfirmed: "✓ Płatność potwierdzona",
  readyToDownload: "Twój 7-stronicowy plan PDF jest gotowy do pobrania.",
  checkDownloads: "Sprawdź swoje pobrane pliki: ClearMyDebt-Plan.pdf",
  generatingPdf: "Generowanie PDF...", downloadAgain: "Pobierz ponownie", downloadYourPdf: "Pobierz swój PDF",
  paymentUnavailable: "Płatność niedostępna. Spróbuj ponownie.",
  pdfLoading: "Ładowanie biblioteki PDF, proszę czekać...",
  sampleTitle: "Przykład — Miesięczny harmonogram płatności",
  sampleSubtitle: "Strona 3 z 7 · Twój pełny plan zawiera wszystkie długi i metody",
  sampleMonth: "Miesiąc", samplePayment: "Płatność", samplePrincipal: "Kapitał",
  sampleInterest: "Odsetki", sampleBalance: "Saldo",
  sampleFullPlan: n => `✦ Kompletny plan: ${n} długi · 4 metody porównane · analiza konsolidacji`,
  getFullPlanBtn: price => `Kompletny plan 7 stron — ${price}`,
  secureInstant: "Bezpieczne · Natychmiastowe pobieranie · Bez rejestracji",
  copyright: "© 2026 Weelco Inc. Wszelkie prawa zastrzeżone. Wyłącznie do celów informacyjnych. Nie jest to doradztwo finansowe.",
};

const cs = {
  headerTitle: "Kdy budete bez dluhů?",
  headerSubtitle: "Zadejte své dluhy · Získejte personalizovaný plán během sekund",
  yourDebts: "Vaše dluhy",
  colType: "Typ", colBalance: "Zůstatek", colRate: "Úrok %", colMinPay: "Min. splátka",
  placeholderBalance: "Zůstatek", placeholderRate: "Úrok %", placeholderMin: "Min.",
  selectType: "Vyberte typ...", addDebt: "+ Přidat dluh",
  required: "Povinné",
  minPaymentError: min => `Minimální splátka musí být vyšší než ${min}/měs. (měsíční úrok)`,
  extraTitle: "Scénáře extra plateb",
  conservative: "Konzervativní", balanced: "Vyvážený", aggressive: "Agresivní",
  extraHint: "I 1000 Kč navíc měsíčně vám může ušetřit tisíce na úrocích",
  totalDebt: "Celkový dluh",
  calculateBtn: "Vypočítat datum splacení",
  freeNoSignup: "Zdarma · Bez registrace · Okamžité výsledky",
  methodAvalanche: "Avalanche", methodSnowball: "Sněhová koule",
  methodHighbal: "Vysoký zůstatek", methodLowint: "Nízký úrok",
  methodAvalancheDesc: "Nejvyšší úrok nejdříve", methodSnowballDesc: "Nejnižší zůstatek nejdříve",
  methodHighbalDesc: "Nejvyšší zůstatek nejdříve", methodLowintDesc: "Nejnižší úrok nejdříve",
  resultHeaderTitle: "Váš plán splacení dluhů",
  debtFreeIn: "Bez dluhů za",
  withExtraMethod: (extra, method) => `s ${extra}/měs. navíc · metoda ${method}`,
  chartToday: "Dnes", chartDebtFree: "Bez dluhů",
  totalInterest: "Celkový úrok", totalDebtLabel: "Celkový dluh", totalPaid: "Celkem zaplaceno",
  methodComparison: "Srovnání metod", interestLabel: "úrok",
  editDebts: "Upravit dluhy",
  getFullPlan: "Získejte kompletní 7stránkový PDF plán",
  pdfItem1: "Přehled vašich dluhů — kde dnes stojíte",
  pdfItem2: "Všechny 4 metody porovnány — Avalanche, Sněhová koule, Vysoký zůstatek, Nízký úrok",
  pdfItem3: "Který dluh řešit jako první — a proč",
  pdfItem4: (a, b, c) => `Co kdybyste platili 0, ${a}, ${b} nebo ${c} navíc?`,
  pdfItem5: "Měsíční splátkový kalendář",
  pdfItem6: "Konsolidace dluhů — vyplatí se pro vás?",
  pdfItem7: "Váš akční plán + FAQ",
  priceBadge: "jednorázově",
  saves: amt => `Ušetří ${amt}`,
  downloadPdfBtn: "📄 Stáhnout PDF plán",
  notSureYet: "Ještě nejste jistí?", seeWhatsInside: "Zobrazit obsah",
  paymentConfirmed: "✓ Platba potvrzena",
  readyToDownload: "Váš 7stránkový PDF plán je připraven ke stažení.",
  checkDownloads: "Zkontrolujte stažené soubory pro ClearMyDebt-Plan.pdf",
  generatingPdf: "Generování PDF...", downloadAgain: "Stáhnout znovu", downloadYourPdf: "Stáhnout PDF",
  paymentUnavailable: "Platba nedostupná. Zkuste to znovu.",
  pdfLoading: "Načítání knihovny PDF, čekejte prosím...",
  sampleTitle: "Ukázka — Měsíční splátkový kalendář",
  sampleSubtitle: "Strana 3 ze 7 · Váš kompletní plán zahrnuje všechny dluhy a metody",
  sampleMonth: "Měsíc", samplePayment: "Splátka", samplePrincipal: "Jistina",
  sampleInterest: "Úrok", sampleBalance: "Zůstatek",
  sampleFullPlan: n => `✦ Kompletní plán: ${n} dluhů · 4 metody porovnány · analýza konsolidace`,
  getFullPlanBtn: price => `Kompletní 7stránkový plán — ${price}`,
  secureInstant: "Bezpečné · Okamžité stažení · Bez registrace",
  copyright: "© 2026 Weelco Inc. Všechna práva vyhrazena. Pouze pro informační účely. Není to finanční poradenství.",
};

const hu = {
  headerTitle: "Mikor leszel adósságmentes?",
  headerSubtitle: "Add meg adósságaidat · Kapj személyre szabott tervet másodpercek alatt",
  yourDebts: "Adósságaid",
  colType: "Típus", colBalance: "Egyenleg", colRate: "Kamat %", colMinPay: "Min. törlesztő",
  placeholderBalance: "Egyenleg", placeholderRate: "Kamat %", placeholderMin: "Min.",
  selectType: "Típus kiválasztása...", addDebt: "+ Adósság hozzáadása",
  required: "Kötelező",
  minPaymentError: min => `A minimális törlesztőnek nagyobbnak kell lennie mint ${min}/hó (havi kamat)`,
  extraTitle: "Extra fizetési forgatókönyvek",
  conservative: "Konzervatív", balanced: "Kiegyensúlyozott", aggressive: "Agresszív",
  extraHint: "Már havi 5000 Ft extra is ezreket takaríthat meg a kamatokon",
  totalDebt: "Összes adósság",
  calculateBtn: "Törlesztési dátumom kiszámítása",
  freeNoSignup: "Ingyenes · Regisztráció nélkül · Azonnali eredmények",
  methodAvalanche: "Lavina", methodSnowball: "Hógolyó",
  methodHighbal: "Magas egyenleg", methodLowint: "Alacsony kamat",
  methodAvalancheDesc: "Legmagasabb kamat először", methodSnowballDesc: "Legkisebb egyenleg először",
  methodHighbalDesc: "Legmagasabb egyenleg először", methodLowintDesc: "Legalacsonyabb kamat először",
  resultHeaderTitle: "Adósságmentes tervem",
  debtFreeIn: "Adósságmentes",
  withExtraMethod: (extra, method) => `${extra}/hó extra · ${method} módszer`,
  chartToday: "Ma", chartDebtFree: "Adósságmentes",
  totalInterest: "Összes kamat", totalDebtLabel: "Összes adósság", totalPaid: "Összesen fizetve",
  methodComparison: "Módszerek összehasonlítása", interestLabel: "kamat",
  editDebts: "Adósságok szerkesztése",
  getFullPlan: "Szerezd meg a teljes 7 oldalas PDF tervedet",
  pdfItem1: "Adósság-pillanatképed — ahol ma állsz",
  pdfItem2: "Mind a 4 módszer összehasonlítva — Lavina, Hógolyó, Magas egyenleg, Alacsony kamat",
  pdfItem3: "Melyik adósságot érdemes először megtámadni — és miért",
  pdfItem4: (a, b, c) => `Mi lenne, ha 0, ${a}, ${b} vagy ${c} extraát fizetnél?`,
  pdfItem5: "Havi fizetési ütemterv",
  pdfItem6: "Adósságkonszolidáció — megéri neked?",
  pdfItem7: "Akcióterved + GYIK",
  priceBadge: "egyszeri díj",
  saves: amt => `Megtakarít ${amt}`,
  downloadPdfBtn: "📄 PDF terv letöltése",
  notSureYet: "Még nem biztos?", seeWhatsInside: "Nézd meg a tartalmat",
  paymentConfirmed: "✓ Fizetés megerősítve",
  readyToDownload: "A 7 oldalas PDF terved készen áll a letöltésre.",
  checkDownloads: "Ellenőrizd a letöltéseket: ClearMyDebt-Plan.pdf",
  generatingPdf: "PDF generálása...", downloadAgain: "Újra letöltés", downloadYourPdf: "PDF letöltése",
  paymentUnavailable: "Fizetés nem elérhető. Kérjük próbáld újra.",
  pdfLoading: "PDF könyvtár betöltése, kérjük várj...",
  sampleTitle: "Minta — Havi fizetési ütemterv",
  sampleSubtitle: "3. oldal / 7 · Teljes terved tartalmazza az összes adósságot és módszert",
  sampleMonth: "Hónap", samplePayment: "Törlesztő", samplePrincipal: "Tőke",
  sampleInterest: "Kamat", sampleBalance: "Egyenleg",
  sampleFullPlan: n => `✦ Teljes terv: ${n} adósság · 4 módszer összehasonlítva · konszolidációs elemzés`,
  getFullPlanBtn: price => `Teljes 7 oldalas terv — ${price}`,
  secureInstant: "Biztonságos · Azonnali letöltés · Regisztráció nélkül",
  copyright: "© 2026 Weelco Inc. Minden jog fenntartva. Kizárólag tájékoztatási célokra. Nem pénzügyi tanácsadás.",
};

const ro = {
  headerTitle: "Când vei fi fără datorii?",
  headerSubtitle: "Introduceți datoriile · Obțineți un plan personalizat în câteva secunde",
  yourDebts: "Datoriile tale",
  colType: "Tip", colBalance: "Sold", colRate: "Dobândă %", colMinPay: "Plată min.",
  placeholderBalance: "Sold", placeholderRate: "Dobândă %", placeholderMin: "Min.",
  selectType: "Selectați tipul...", addDebt: "+ Adăugați datorie",
  required: "Obligatoriu",
  minPaymentError: min => `Plata minimă trebuie să fie mai mare de ${min}/lună (dobândă lunară)`,
  extraTitle: "Scenarii de plată suplimentară",
  conservative: "Conservator", balanced: "Echilibrat", aggressive: "Agresiv",
  extraHint: "Chiar și 250 lei extra pe lună vă pot economisi mii în dobânzi",
  totalDebt: "Total datorii",
  calculateBtn: "Calculați data de achitare",
  freeNoSignup: "Gratuit · Fără înregistrare · Rezultate instantanee",
  methodAvalanche: "Avalanșă", methodSnowball: "Bulgăre de zăpadă",
  methodHighbal: "Sold ridicat", methodLowint: "Dobândă mică",
  methodAvalancheDesc: "Dobânda cea mai mare mai întâi", methodSnowballDesc: "Soldul cel mai mic mai întâi",
  methodHighbalDesc: "Soldul cel mai mare mai întâi", methodLowintDesc: "Dobânda cea mai mică mai întâi",
  resultHeaderTitle: "Planul tău de achitare a datoriilor",
  debtFreeIn: "Fără datorii în",
  withExtraMethod: (extra, method) => `cu ${extra}/lună extra · metoda ${method}`,
  chartToday: "Azi", chartDebtFree: "Fără datorii",
  totalInterest: "Total dobânzi", totalDebtLabel: "Total datorii", totalPaid: "Total plătit",
  methodComparison: "Compararea metodelor", interestLabel: "dobândă",
  editDebts: "Editați datoriile",
  getFullPlan: "Obțineți planul PDF complet de 7 pagini",
  pdfItem1: "Instantaneul datoriilor tale — unde te afli azi",
  pdfItem2: "Toate 4 metode comparate — Avalanșă, Bulgăre de zăpadă, Sold ridicat, Dobândă mică",
  pdfItem3: "Ce datorie să ataci mai întâi — și de ce",
  pdfItem4: (a, b, c) => `Ce s-ar întâmpla dacă ai plăti 0, ${a}, ${b} sau ${c} extra?`,
  pdfItem5: "Programul de plată lună cu lună",
  pdfItem6: "Consolidarea datoriilor — merită pentru tine?",
  pdfItem7: "Planul tău de acțiune + Întrebări frecvente",
  priceBadge: "plată unică",
  saves: amt => `Economisește ${amt}`,
  downloadPdfBtn: "📄 Descărcați planul PDF",
  notSureYet: "Nu ești sigur încă?", seeWhatsInside: "Vezi ce conține",
  paymentConfirmed: "✓ Plată confirmată",
  readyToDownload: "Planul tău PDF de 7 pagini este gata de descărcat.",
  checkDownloads: "Verificați descărcările pentru ClearMyDebt-Plan.pdf",
  generatingPdf: "Se generează PDF...", downloadAgain: "Descărcați din nou", downloadYourPdf: "Descărcați PDF-ul",
  paymentUnavailable: "Plată indisponibilă. Vă rugăm să încercați din nou.",
  pdfLoading: "Se încarcă biblioteca PDF, vă rugăm așteptați...",
  sampleTitle: "Exemplu — Program lunar de plată",
  sampleSubtitle: "Pagina 3 din 7 · Planul complet include toate datoriile și metodele",
  sampleMonth: "Lună", samplePayment: "Plată", samplePrincipal: "Principal",
  sampleInterest: "Dobândă", sampleBalance: "Sold",
  sampleFullPlan: n => `✦ Plan complet: ${n} datorii · 4 metode comparate · analiză de consolidare`,
  getFullPlanBtn: price => `Plan complet de 7 pagini — ${price}`,
  secureInstant: "Securizat · Descărcare instantanee · Fără înregistrare",
  copyright: "© 2026 Weelco Inc. Toate drepturile rezervate. Doar în scop informativ. Nu este consiliere financiară.",
};

const sk = {
  headerTitle: "Kedy budete bez dlhov?",
  headerSubtitle: "Zadajte svoje dlhy · Získajte personalizovaný plán za sekundy",
  yourDebts: "Vaše dlhy",
  colType: "Typ", colBalance: "Zostatok", colRate: "Úrok %", colMinPay: "Min. splátka",
  placeholderBalance: "Zostatok", placeholderRate: "Úrok %", placeholderMin: "Min.",
  selectType: "Vyberte typ...", addDebt: "+ Pridať dlh",
  required: "Povinné",
  minPaymentError: min => `Minimálna splátka musí byť vyššia ako ${min}/mes. (mesačný úrok)`,
  extraTitle: "Scenáre extra platieb",
  conservative: "Konzervatívny", balanced: "Vyvážený", aggressive: "Agresívny",
  extraHint: "Aj 50 € navyše mesačne môže ušetriť tisíce na úrokoch",
  totalDebt: "Celkový dlh",
  calculateBtn: "Vypočítať dátum splatenia",
  freeNoSignup: "Zadarmo · Bez registrácie · Okamžité výsledky",
  methodAvalanche: "Avalanche", methodSnowball: "Snehová guľa",
  methodHighbal: "Vysoký zostatok", methodLowint: "Nízky úrok",
  methodAvalancheDesc: "Najvyšší úrok najskôr", methodSnowballDesc: "Najnižší zostatok najskôr",
  methodHighbalDesc: "Najvyšší zostatok najskôr", methodLowintDesc: "Najnižší úrok najskôr",
  resultHeaderTitle: "Váš plán splatenia dlhov",
  debtFreeIn: "Bez dlhov za",
  withExtraMethod: (extra, method) => `s ${extra}/mes. navyše · metóda ${method}`,
  chartToday: "Dnes", chartDebtFree: "Bez dlhov",
  totalInterest: "Celkový úrok", totalDebtLabel: "Celkový dlh", totalPaid: "Celkom zaplatené",
  methodComparison: "Porovnanie metód", interestLabel: "úrok",
  editDebts: "Upraviť dlhy",
  getFullPlan: "Získajte kompletný 7-stranový PDF plán",
  pdfItem1: "Prehľad vašich dlhov — kde dnes stojíte",
  pdfItem2: "Všetky 4 metódy porovnané — Avalanche, Snehová guľa, Vysoký zostatok, Nízky úrok",
  pdfItem3: "Ktorý dlh riešiť ako prvý — a prečo",
  pdfItem4: (a, b, c) => `Čo keby ste platili 0, ${a}, ${b} alebo ${c} navyše?`,
  pdfItem5: "Mesačný splátkový kalendár",
  pdfItem6: "Konsolidácia dlhov — oplatí sa pre vás?",
  pdfItem7: "Váš akčný plán + FAQ",
  priceBadge: "jednorazovo",
  saves: amt => `Ušetrí ${amt}`,
  downloadPdfBtn: "📄 Stiahnuť PDF plán",
  notSureYet: "Ešte si nie ste istí?", seeWhatsInside: "Zobraziť obsah",
  paymentConfirmed: "✓ Platba potvrdená",
  readyToDownload: "Váš 7-stranový PDF plán je pripravený na stiahnutie.",
  checkDownloads: "Skontrolujte stiahnuté súbory: ClearMyDebt-Plan.pdf",
  generatingPdf: "Generovanie PDF...", downloadAgain: "Stiahnuť znovu", downloadYourPdf: "Stiahnuť PDF",
  paymentUnavailable: "Platba nedostupná. Skúste to znovu.",
  pdfLoading: "Načítanie knižnice PDF, čakajte prosím...",
  sampleTitle: "Ukážka — Mesačný splátkový kalendár",
  sampleSubtitle: "Strana 3 zo 7 · Váš kompletný plán zahŕňa všetky dlhy a metódy",
  sampleMonth: "Mesiac", samplePayment: "Splátka", samplePrincipal: "Istina",
  sampleInterest: "Úrok", sampleBalance: "Zostatok",
  sampleFullPlan: n => `✦ Kompletný plán: ${n} dlhov · 4 metódy porovnané · analýza konsolidácie`,
  getFullPlanBtn: price => `Kompletný 7-stranový plán — ${price}`,
  secureInstant: "Bezpečné · Okamžité stiahnutie · Bez registrácie",
  copyright: "© 2026 Weelco Inc. Všetky práva vyhradené. Len na informačné účely. Nie je to finančné poradenstvo.",
};

const sl = {
  headerTitle: "Kdaj boste brez dolgov?",
  headerSubtitle: "Vnesite svoje dolgove · Prejmite osebni načrt v sekundah",
  yourDebts: "Vaši dolgovi",
  colType: "Vrsta", colBalance: "Stanje", colRate: "Obrestna mera %", colMinPay: "Min. obrok",
  placeholderBalance: "Stanje", placeholderRate: "Obrestna mera %", placeholderMin: "Min.",
  selectType: "Izberite vrsto...", addDebt: "+ Dodaj dolg",
  required: "Obvezno",
  minPaymentError: min => `Minimalni obrok mora biti večji od ${min}/mes. (mesečne obresti)`,
  extraTitle: "Scenariji dodatnih plačil",
  conservative: "Konservativen", balanced: "Uravnotežen", aggressive: "Agresiven",
  extraHint: "Že 50 € extra na mesec vam lahko prihrani tisoče pri obrestih",
  totalDebt: "Skupni dolg",
  calculateBtn: "Izračunajte datum odplačila",
  freeNoSignup: "Brezplačno · Brez registracije · Takojšnji rezultati",
  methodAvalanche: "Plaz", methodSnowball: "Snežna kepa",
  methodHighbal: "Visoko stanje", methodLowint: "Nizke obresti",
  methodAvalancheDesc: "Najvišja obrestna mera najprej", methodSnowballDesc: "Najnižje stanje najprej",
  methodHighbalDesc: "Najvišje stanje najprej", methodLowintDesc: "Najnižja obrestna mera najprej",
  resultHeaderTitle: "Vaš načrt za odplačilo dolgov",
  debtFreeIn: "Brez dolgov čez",
  withExtraMethod: (extra, method) => `z ${extra}/mes. extra · metoda ${method}`,
  chartToday: "Danes", chartDebtFree: "Brez dolgov",
  totalInterest: "Skupne obresti", totalDebtLabel: "Skupni dolg", totalPaid: "Skupaj plačano",
  methodComparison: "Primerjava metod", interestLabel: "obresti",
  editDebts: "Uredi dolgove",
  getFullPlan: "Pridobite popoln 7-stranski PDF načrt",
  pdfItem1: "Pregled vaših dolgov — kje ste danes",
  pdfItem2: "Vse 4 metode primerjane — Plaz, Snežna kepa, Visoko stanje, Nizke obresti",
  pdfItem3: "Kateri dolg napasti prvi — in zakaj",
  pdfItem4: (a, b, c) => `Kaj če plačate 0, ${a}, ${b} ali ${c} extra?`,
  pdfItem5: "Mesečni plačilni načrt",
  pdfItem6: "Konsolidacija dolgov — se splača za vas?",
  pdfItem7: "Vaš akcijski načrt + FAQ",
  priceBadge: "enkratno plačilo",
  saves: amt => `Prihrani ${amt}`,
  downloadPdfBtn: "📄 Prenesite PDF načrt",
  notSureYet: "Še niste prepričani?", seeWhatsInside: "Oglejte si vsebino",
  paymentConfirmed: "✓ Plačilo potrjeno",
  readyToDownload: "Vaš 7-stranski PDF načrt je pripravljen za prenos.",
  checkDownloads: "Preverite prenose za ClearMyDebt-Plan.pdf",
  generatingPdf: "Ustvarjanje PDF...", downloadAgain: "Prenesi znova", downloadYourPdf: "Prenesite PDF",
  paymentUnavailable: "Plačilo ni na voljo. Poskusite znova.",
  pdfLoading: "Nalaganje knjižnice PDF, prosim počakajte...",
  sampleTitle: "Primer — Mesečni plačilni načrt",
  sampleSubtitle: "Stran 3 od 7 · Vaš celoten načrt vključuje vse dolgove in metode",
  sampleMonth: "Mesec", samplePayment: "Plačilo", samplePrincipal: "Glavnica",
  sampleInterest: "Obresti", sampleBalance: "Stanje",
  sampleFullPlan: n => `✦ Popoln načrt: ${n} dolgov · 4 metode primerjane · analiza konsolidacije`,
  getFullPlanBtn: price => `Popoln 7-stranski načrt — ${price}`,
  secureInstant: "Varno · Takojšen prenos · Brez registracije",
  copyright: "© 2026 Weelco Inc. Vse pravice pridržane. Samo v informativne namene. Ni finančni nasvet.",
};

const et = {
  headerTitle: "Millal oled võlgadest vaba?",
  headerSubtitle: "Sisesta oma võlad · Saa personaalne plaan sekunditega",
  yourDebts: "Sinu võlad",
  colType: "Tüüp", colBalance: "Saldo", colRate: "Intress %", colMinPay: "Min. makse",
  placeholderBalance: "Saldo", placeholderRate: "Intress %", placeholderMin: "Min.",
  selectType: "Vali tüüp...", addDebt: "+ Lisa võlg",
  required: "Kohustuslik",
  minPaymentError: min => `Minimaalmakse peab olema suurem kui ${min}/kuu (kuuintress)`,
  extraTitle: "Lisemaksestsenaariumid",
  conservative: "Konservatiivne", balanced: "Tasakaalustatud", aggressive: "Agressiivne",
  extraHint: "Isegi 50 € lisaks kuus võib säästa tuhandeid intressis",
  totalDebt: "Kogu võlg",
  calculateBtn: "Arvuta tagasimaksmise kuupäev",
  freeNoSignup: "Tasuta · Registreerimata · Kohesed tulemused",
  methodAvalanche: "Laviin", methodSnowball: "Lumepall",
  methodHighbal: "Kõrge saldo", methodLowint: "Madal intress",
  methodAvalancheDesc: "Kõrgeim intress esmalt", methodSnowballDesc: "Madalaim saldo esmalt",
  methodHighbalDesc: "Kõrgeim saldo esmalt", methodLowintDesc: "Madalaim intress esmalt",
  resultHeaderTitle: "Sinu võlavabaduse plaan",
  debtFreeIn: "Võlgadest vaba",
  withExtraMethod: (extra, method) => `${extra}/kuu lisaks · ${method} meetod`,
  chartToday: "Täna", chartDebtFree: "Võlgadeta",
  totalInterest: "Kogu intress", totalDebtLabel: "Kogu võlg", totalPaid: "Kokku makstud",
  methodComparison: "Meetodite võrdlus", interestLabel: "intress",
  editDebts: "Muuda võlgu",
  getFullPlan: "Hangi täielik 7-leheküljeline PDF-plaan",
  pdfItem1: "Sinu võlgade ülevaade — kus sa täna oled",
  pdfItem2: "Kõik 4 meetodit võrreldud — Laviin, Lumepall, Kõrge saldo, Madal intress",
  pdfItem3: "Millist võlga esmalt rünnata — ja miks",
  pdfItem4: (a, b, c) => `Mis juhtuks kui maksad 0, ${a}, ${b} või ${c} lisaks?`,
  pdfItem5: "Kuupõhine maksegraafik",
  pdfItem6: "Võlgade konsolideerimine — kas see tasub end ära?",
  pdfItem7: "Sinu tegevusplaan + KKK",
  priceBadge: "ühekordne",
  saves: amt => `Säästab ${amt}`,
  downloadPdfBtn: "📄 Laadi PDF-plaan alla",
  notSureYet: "Pole veel kindel?", seeWhatsInside: "Vaata sisu",
  paymentConfirmed: "✓ Makse kinnitatud",
  readyToDownload: "Sinu 7-leheküljeline PDF-plaan on allalaadimiseks valmis.",
  checkDownloads: "Kontrolli allalaadimisi: ClearMyDebt-Plan.pdf",
  generatingPdf: "PDF loomine...", downloadAgain: "Laadi uuesti alla", downloadYourPdf: "Laadi PDF alla",
  paymentUnavailable: "Makse pole saadaval. Proovi uuesti.",
  pdfLoading: "PDF-teegi laadimine, palun oota...",
  sampleTitle: "Näidis — Kuupõhine maksegraafik",
  sampleSubtitle: "Lehekülg 3/7 · Täielik plaan sisaldab kõiki võlgu ja meetodeid",
  sampleMonth: "Kuu", samplePayment: "Makse", samplePrincipal: "Põhiosa",
  sampleInterest: "Intress", sampleBalance: "Saldo",
  sampleFullPlan: n => `✦ Täielik plaan: ${n} võlga · 4 meetodit võrreldud · konsolideerimisanalüüs`,
  getFullPlanBtn: price => `Täielik 7-leheküljeline plaan — ${price}`,
  secureInstant: "Turvaline · Kohene allalaadimine · Registreerimata",
  copyright: "© 2026 Weelco Inc. Kõik õigused kaitstud. Ainult teavituslikul eesmärgil. Ei ole finantsnõustamine.",
};

const lv = {
  headerTitle: "Kad būsi brīvs no parādiem?",
  headerSubtitle: "Ievadi savus parādus · Saņem personalizētu plānu sekundēs",
  yourDebts: "Tavi parādi",
  colType: "Tips", colBalance: "Atlikums", colRate: "Procenti %", colMinPay: "Min. maks.",
  placeholderBalance: "Atlikums", placeholderRate: "Procenti %", placeholderMin: "Min.",
  selectType: "Izvēlies tipu...", addDebt: "+ Pievienot parādu",
  required: "Obligāts",
  minPaymentError: min => `Minimālajam maksājumam jābūt lielākam par ${min}/mēn. (ikmēneša procenti)`,
  extraTitle: "Papildu maksājumu scenāriji",
  conservative: "Konservatīvs", balanced: "Sabalansēts", aggressive: "Agresīvs",
  extraHint: "Pat 50 € papildus mēnesī var ietaupīt tūkstošus procentos",
  totalDebt: "Kopējais parāds",
  calculateBtn: "Aprēķināt atmaksas datumu",
  freeNoSignup: "Bezmaksas · Bez reģistrācijas · Tūlītēji rezultāti",
  methodAvalanche: "Lavīna", methodSnowball: "Sniega bumba",
  methodHighbal: "Augsts atlikums", methodLowint: "Zemi procenti",
  methodAvalancheDesc: "Augstākie procenti pirmie", methodSnowballDesc: "Zemākais atlikums pirmais",
  methodHighbalDesc: "Augstākais atlikums pirmais", methodLowintDesc: "Zemākie procenti pirmie",
  resultHeaderTitle: "Tavs parādu atmaksas plāns",
  debtFreeIn: "Brīvs no parādiem pēc",
  withExtraMethod: (extra, method) => `ar ${extra}/mēn. papildus · ${method} metode`,
  chartToday: "Šodien", chartDebtFree: "Bez parādiem",
  totalInterest: "Kopējie procenti", totalDebtLabel: "Kopējais parāds", totalPaid: "Kopā samaksāts",
  methodComparison: "Metožu salīdzinājums", interestLabel: "procenti",
  editDebts: "Rediģēt parādus",
  getFullPlan: "Iegūsti pilnu 7 lappušu PDF plānu",
  pdfItem1: "Tavs parādu pārskats — kur esi šodien",
  pdfItem2: "Visas 4 metodes salīdzinātas — Lavīna, Sniega bumba, Augsts atlikums, Zemi procenti",
  pdfItem3: "Kuru parādu uzbrukt pirmajam — un kāpēc",
  pdfItem4: (a, b, c) => `Ko darītu, ja maksātu 0, ${a}, ${b} vai ${c} papildus?`,
  pdfItem5: "Ikmēneša maksājumu grafiks",
  pdfItem6: "Parādu konsolidācija — vai tā ir vērts tev?",
  pdfItem7: "Tavs rīcības plāns + BUJ",
  priceBadge: "vienreizējs maksājums",
  saves: amt => `Ietaupa ${amt}`,
  downloadPdfBtn: "📄 Lejupielādēt PDF plānu",
  notSureYet: "Vēl neesi pārliecināts?", seeWhatsInside: "Apskatīt saturu",
  paymentConfirmed: "✓ Maksājums apstiprināts",
  readyToDownload: "Tavs 7 lappušu PDF plāns ir gatavs lejupielādei.",
  checkDownloads: "Pārbaudi lejupielādes: ClearMyDebt-Plan.pdf",
  generatingPdf: "PDF ģenerēšana...", downloadAgain: "Lejupielādēt vēlreiz", downloadYourPdf: "Lejupielādēt PDF",
  paymentUnavailable: "Maksājums nav pieejams. Lūdzu mēģini vēlreiz.",
  pdfLoading: "PDF bibliotēkas ielāde, lūdzu gaidi...",
  sampleTitle: "Paraugs — Ikmēneša maksājumu grafiks",
  sampleSubtitle: "3. lappuse no 7 · Pilns plāns ietver visus parādus un metodes",
  sampleMonth: "Mēnesis", samplePayment: "Maksājums", samplePrincipal: "Pamatsumma",
  sampleInterest: "Procenti", sampleBalance: "Atlikums",
  sampleFullPlan: n => `✦ Pilns plāns: ${n} parādi · 4 metodes salīdzinātas · konsolidācijas analīze`,
  getFullPlanBtn: price => `Pilns 7 lappušu plāns — ${price}`,
  secureInstant: "Drošs · Tūlītēja lejupielāde · Bez reģistrācijas",
  copyright: "© 2026 Weelco Inc. Visas tiesības aizsargātas. Tikai informatīviem nolūkiem. Nav finanšu konsultācija.",
};

const lt = {
  headerTitle: "Kada būsi be skolų?",
  headerSubtitle: "Įvesk savo skolas · Gauk personalizuotą planą per sekundes",
  yourDebts: "Tavo skolos",
  colType: "Tipas", colBalance: "Likutis", colRate: "Palūkanos %", colMinPay: "Min. įmoka",
  placeholderBalance: "Likutis", placeholderRate: "Palūkanos %", placeholderMin: "Min.",
  selectType: "Pasirink tipą...", addDebt: "+ Pridėti skolą",
  required: "Privaloma",
  minPaymentError: min => `Minimali įmoka turi būti didesnė nei ${min}/mėn. (mėnesio palūkanos)`,
  extraTitle: "Papildomų mokėjimų scenarijai",
  conservative: "Konservatyvus", balanced: "Subalansuotas", aggressive: "Agresyvus",
  extraHint: "Net 50 € papildomai per mėnesį gali sutaupyti tūkstančius palūkanose",
  totalDebt: "Bendra skola",
  calculateBtn: "Apskaičiuoti grąžinimo datą",
  freeNoSignup: "Nemokamai · Be registracijos · Akimirksniu rezultatai",
  methodAvalanche: "Lavina", methodSnowball: "Sniego kamuolys",
  methodHighbal: "Aukštas likutis", methodLowint: "Žemos palūkanos",
  methodAvalancheDesc: "Aukščiausios palūkanos pirmiausia", methodSnowballDesc: "Mažiausias likutis pirmiausia",
  methodHighbalDesc: "Aukščiausias likutis pirmiausia", methodLowintDesc: "Žemiausios palūkanos pirmiausia",
  resultHeaderTitle: "Tavo skolų grąžinimo planas",
  debtFreeIn: "Be skolų per",
  withExtraMethod: (extra, method) => `su ${extra}/mėn. papildomai · ${method} metodas`,
  chartToday: "Šiandien", chartDebtFree: "Be skolų",
  totalInterest: "Visos palūkanos", totalDebtLabel: "Bendra skola", totalPaid: "Iš viso sumokėta",
  methodComparison: "Metodų palyginimas", interestLabel: "palūkanos",
  editDebts: "Redaguoti skolas",
  getFullPlan: "Gauk išsamų 7 puslapių PDF planą",
  pdfItem1: "Tavo skolų apžvalga — kur esi šiandien",
  pdfItem2: "Visi 4 metodai palyginti — Lavina, Sniego kamuolys, Aukštas likutis, Žemos palūkanos",
  pdfItem3: "Kurią skolą pulti pirmiausia — ir kodėl",
  pdfItem4: (a, b, c) => `Kas būtų jei mokėtum 0, ${a}, ${b} ar ${c} papildomai?`,
  pdfItem5: "Mėnesinis mokėjimų grafikas",
  pdfItem6: "Skolų konsolidavimas — ar verta tau?",
  pdfItem7: "Tavo veiksmų planas + DUK",
  priceBadge: "vienkartinis mokestis",
  saves: amt => `Taupo ${amt}`,
  downloadPdfBtn: "📄 Atsisiųsti PDF planą",
  notSureYet: "Dar neesi tikras?", seeWhatsInside: "Žiūrėti turinį",
  paymentConfirmed: "✓ Mokėjimas patvirtintas",
  readyToDownload: "Tavo 7 puslapių PDF planas paruoštas atsisiųsti.",
  checkDownloads: "Patikrink atsisiuntimus: ClearMyDebt-Plan.pdf",
  generatingPdf: "Generuojamas PDF...", downloadAgain: "Atsisiųsti dar kartą", downloadYourPdf: "Atsisiųsti PDF",
  paymentUnavailable: "Mokėjimas nepasiekiamas. Bandyk dar kartą.",
  pdfLoading: "Įkeliama PDF biblioteka, palaukite...",
  sampleTitle: "Pavyzdys — Mėnesinis mokėjimų grafikas",
  sampleSubtitle: "3 puslapis iš 7 · Pilnas planas apima visas skolas ir metodus",
  sampleMonth: "Mėnuo", samplePayment: "Įmoka", samplePrincipal: "Pagrindinė suma",
  sampleInterest: "Palūkanos", sampleBalance: "Likutis",
  sampleFullPlan: n => `✦ Pilnas planas: ${n} skolų · 4 metodai palyginti · konsolidavimo analizė`,
  getFullPlanBtn: price => `Pilnas 7 puslapių planas — ${price}`,
  secureInstant: "Saugu · Momentinis atsisiuntimas · Be registracijos",
  copyright: "© 2026 Weelco Inc. Visos teisės saugomos. Tik informaciniais tikslais. Tai nėra finansinė konsultacija.",
};

const bg = {
  headerTitle: "Кога ще бъдеш без дългове?",
  headerSubtitle: "Въведи дълговете си · Получи персонализиран план за секунди",
  yourDebts: "Твоите дългове",
  colType: "Тип", colBalance: "Салдо", colRate: "Лихва %", colMinPay: "Мин. вноска",
  placeholderBalance: "Салдо", placeholderRate: "Лихва %", placeholderMin: "Мин.",
  selectType: "Избери тип...", addDebt: "+ Добави дълг",
  required: "Задължително",
  minPaymentError: min => `Минималната вноска трябва да е над ${min}/мес. (месечна лихва)`,
  extraTitle: "Сценарии за допълнителни плащания",
  conservative: "Консервативен", balanced: "Балансиран", aggressive: "Агресивен",
  extraHint: "Дори 100 лв. допълнително на месец може да спести хиляди от лихви",
  totalDebt: "Общ дълг",
  calculateBtn: "Изчисли датата на изплащане",
  freeNoSignup: "Безплатно · Без регистрация · Незабавни резултати",
  methodAvalanche: "Лавина", methodSnowball: "Снежна топка",
  methodHighbal: "Високо салдо", methodLowint: "Ниска лихва",
  methodAvalancheDesc: "Най-висока лихва първо", methodSnowballDesc: "Най-ниско салдо първо",
  methodHighbalDesc: "Най-високо салдо първо", methodLowintDesc: "Най-ниска лихва първо",
  resultHeaderTitle: "Твоят план за свобода от дългове",
  debtFreeIn: "Без дългове след",
  withExtraMethod: (extra, method) => `с ${extra}/мес. допълнително · метод ${method}`,
  chartToday: "Днес", chartDebtFree: "Без дългове",
  totalInterest: "Обща лихва", totalDebtLabel: "Общ дълг", totalPaid: "Общо платено",
  methodComparison: "Сравнение на методи", interestLabel: "лихва",
  editDebts: "Редактирай дълговете",
  getFullPlan: "Вземи пълния 7-страничен PDF план",
  pdfItem1: "Преглед на дълговете ти — къде си днес",
  pdfItem2: "Всички 4 метода сравнени — Лавина, Снежна топка, Високо салдо, Ниска лихва",
  pdfItem3: "Кой дълг да атакуваш първо — и защо",
  pdfItem4: (a, b, c) => `Какво ако платиш 0, ${a}, ${b} или ${c} допълнително?`,
  pdfItem5: "Месечен график на плащанията",
  pdfItem6: "Консолидация на дългове — изгодно ли е за теб?",
  pdfItem7: "Твоят план за действие + ЧЗВ",
  priceBadge: "еднократно",
  saves: amt => `Спестява ${amt}`,
  downloadPdfBtn: "📄 Изтегли PDF план",
  notSureYet: "Още не си сигурен?", seeWhatsInside: "Виж съдържанието",
  paymentConfirmed: "✓ Плащането е потвърдено",
  readyToDownload: "Твоят 7-страничен PDF план е готов за изтегляне.",
  checkDownloads: "Провери изтеглените файлове: ClearMyDebt-Plan.pdf",
  generatingPdf: "Генериране на PDF...", downloadAgain: "Изтегли отново", downloadYourPdf: "Изтегли PDF",
  paymentUnavailable: "Плащането е недостъпно. Моля опитай отново.",
  pdfLoading: "Зареждане на PDF библиотека, моля изчакай...",
  sampleTitle: "Пример — Месечен график на плащанията",
  sampleSubtitle: "Страница 3 от 7 · Пълният план включва всички дългове и методи",
  sampleMonth: "Месец", samplePayment: "Вноска", samplePrincipal: "Главница",
  sampleInterest: "Лихва", sampleBalance: "Салдо",
  sampleFullPlan: n => `✦ Пълен план: ${n} дълга · 4 метода сравнени · анализ на консолидация`,
  getFullPlanBtn: price => `Пълен 7-страничен план — ${price}`,
  secureInstant: "Сигурно · Незабавно изтегляне · Без регистрация",
  copyright: "© 2026 Weelco Inc. Всички права запазени. Само за информационни цели. Не е финансов съвет.",
};

const ja = {
  headerTitle: "いつ借金がゼロになりますか？",
  headerSubtitle: "借金を入力 · 数秒でパーソナライズされたプランを取得",
  yourDebts: "あなたの借金",
  colType: "種類", colBalance: "残高", colRate: "金利 %", colMinPay: "最低返済額",
  placeholderBalance: "残高", placeholderRate: "金利 %", placeholderMin: "最低額",
  selectType: "種類を選択...", addDebt: "+ 借金を追加",
  required: "必須",
  minPaymentError: min => `最低返済額は${min}/月（月利）を超える必要があります`,
  extraTitle: "追加返済シナリオ",
  conservative: "保守的", balanced: "バランス型", aggressive: "積極的",
  extraHint: "月々5,000円の追加返済で利息を数十万円節約できます",
  totalDebt: "借金合計",
  calculateBtn: "返済完了日を計算する",
  freeNoSignup: "無料 · 登録不要 · 即時結果",
  methodAvalanche: "アバランチ法", methodSnowball: "スノーボール法",
  methodHighbal: "高残高優先", methodLowint: "低金利優先",
  methodAvalancheDesc: "高金利から返済", methodSnowballDesc: "低残高から返済",
  methodHighbalDesc: "高残高から返済", methodLowintDesc: "低金利から返済",
  resultHeaderTitle: "あなたの借金返済プラン",
  debtFreeIn: "借金ゼロまで",
  withExtraMethod: (extra, method) => `月${extra}追加 · ${method}`,
  chartToday: "今日", chartDebtFree: "借金ゼロ",
  totalInterest: "利息合計", totalDebtLabel: "借金合計", totalPaid: "総支払額",
  methodComparison: "返済方法の比較", interestLabel: "利息",
  editDebts: "借金を編集",
  getFullPlan: "7ページの完全PDFプランを入手",
  pdfItem1: "借金の現状 — 今日の状況",
  pdfItem2: "4つの方法を比較 — アバランチ、スノーボール、高残高、低金利",
  pdfItem3: "最初に返済すべき借金 — その理由",
  pdfItem4: (a, b, c) => `追加返済が0、${a}、${b}、${c}の場合は？`,
  pdfItem5: "月別返済スケジュール",
  pdfItem6: "借金の一本化 — あなたにとって有効か？",
  pdfItem7: "行動計画 + よくある質問",
  priceBadge: "一回払い",
  saves: amt => `${amt}節約`,
  downloadPdfBtn: "📄 PDFプランをダウンロード",
  notSureYet: "まだ迷っていますか？", seeWhatsInside: "内容を見る",
  paymentConfirmed: "✓ 決済が確認されました",
  readyToDownload: "7ページのPDFプランがダウンロード可能です。",
  checkDownloads: "ダウンロードフォルダのClearMyDebt-Plan.pdfを確認してください",
  generatingPdf: "PDF生成中...", downloadAgain: "再ダウンロード", downloadYourPdf: "PDFをダウンロード",
  paymentUnavailable: "決済できません。もう一度お試しください。",
  pdfLoading: "PDFライブラリ読み込み中、しばらくお待ちください...",
  sampleTitle: "サンプル — 月別返済スケジュール",
  sampleSubtitle: "3ページ目/7 · 完全プランには全借金と全方法が含まれます",
  sampleMonth: "月", samplePayment: "返済額", samplePrincipal: "元金",
  sampleInterest: "利息", sampleBalance: "残高",
  sampleFullPlan: n => `✦ 完全プラン：${n}件の借金 · 4つの方法を比較 · 一本化分析`,
  getFullPlanBtn: price => `7ページ完全プラン — ${price}`,
  secureInstant: "安全 · 即時ダウンロード · 登録不要",
  copyright: "© 2026 Weelco Inc. 無断転載禁止。情報提供のみを目的としています。金融アドバイスではありません。",
};

const th = {
  headerTitle: "คุณจะปลอดหนี้เมื่อไหร่?",
  headerSubtitle: "ป้อนหนี้ของคุณ · รับแผนส่วนตัวในไม่กี่วินาที",
  yourDebts: "หนี้ของคุณ",
  colType: "ประเภท", colBalance: "ยอดคงเหลือ", colRate: "ดอกเบี้ย %", colMinPay: "จ่ายขั้นต่ำ",
  placeholderBalance: "ยอดคงเหลือ", placeholderRate: "ดอกเบี้ย %", placeholderMin: "ขั้นต่ำ",
  selectType: "เลือกประเภท...", addDebt: "+ เพิ่มหนี้",
  required: "จำเป็น",
  minPaymentError: min => `การชำระขั้นต่ำต้องมากกว่า ${min}/เดือน (ดอกเบี้ยรายเดือน)`,
  extraTitle: "สถานการณ์การชำระเพิ่มเติม",
  conservative: "อนุรักษ์นิยม", balanced: "สมดุล", aggressive: "เชิงรุก",
  extraHint: "แค่ ฿500 เพิ่มต่อเดือน ก็ช่วยประหยัดดอกเบี้ยได้หลายหมื่นบาท",
  totalDebt: "หนี้รวม",
  calculateBtn: "คำนวณวันปลอดหนี้ของฉัน",
  freeNoSignup: "ฟรี · ไม่ต้องสมัครสมาชิก · ผลลัพธ์ทันที",
  methodAvalanche: "อาวาลันช์", methodSnowball: "สโนว์บอล",
  methodHighbal: "ยอดสูงก่อน", methodLowint: "ดอกเบี้ยต่ำก่อน",
  methodAvalancheDesc: "ดอกเบี้ยสูงสุดก่อน", methodSnowballDesc: "ยอดต่ำสุดก่อน",
  methodHighbalDesc: "ยอดสูงสุดก่อน", methodLowintDesc: "ดอกเบี้ยต่ำสุดก่อน",
  resultHeaderTitle: "แผนปลอดหนี้ของคุณ",
  debtFreeIn: "ปลอดหนี้ใน",
  withExtraMethod: (extra, method) => `จ่ายเพิ่ม ${extra}/เดือน · วิธี${method}`,
  chartToday: "วันนี้", chartDebtFree: "ปลอดหนี้",
  totalInterest: "ดอกเบี้ยรวม", totalDebtLabel: "หนี้รวม", totalPaid: "จ่ายรวม",
  methodComparison: "เปรียบเทียบวิธี", interestLabel: "ดอกเบี้ย",
  editDebts: "แก้ไขหนี้",
  getFullPlan: "รับแผน PDF 7 หน้าสมบูรณ์",
  pdfItem1: "ภาพรวมหนี้ของคุณ — ตำแหน่งที่คุณอยู่วันนี้",
  pdfItem2: "เปรียบเทียบ 4 วิธี — อาวาลันช์ สโนว์บอล ยอดสูง ดอกเบี้ยต่ำ",
  pdfItem3: "หนี้ไหนที่ควรจัดการก่อน — และทำไม",
  pdfItem4: (a, b, c) => `จะเป็นอย่างไรถ้าจ่ายเพิ่ม 0, ${a}, ${b} หรือ ${c}?`,
  pdfItem5: "ตารางการชำระรายเดือน",
  pdfItem6: "การรวมหนี้ — คุ้มค่าสำหรับคุณไหม?",
  pdfItem7: "แผนปฏิบัติการ + คำถามที่พบบ่อย",
  priceBadge: "จ่ายครั้งเดียว",
  saves: amt => `ประหยัด ${amt}`,
  downloadPdfBtn: "📄 ดาวน์โหลดแผน PDF",
  notSureYet: "ยังไม่แน่ใจ?", seeWhatsInside: "ดูเนื้อหา",
  paymentConfirmed: "✓ ยืนยันการชำระเงินแล้ว",
  readyToDownload: "แผน PDF 7 หน้าของคุณพร้อมดาวน์โหลด",
  checkDownloads: "ตรวจสอบไฟล์ที่ดาวน์โหลด: ClearMyDebt-Plan.pdf",
  generatingPdf: "กำลังสร้าง PDF...", downloadAgain: "ดาวน์โหลดอีกครั้ง", downloadYourPdf: "ดาวน์โหลด PDF",
  paymentUnavailable: "ไม่สามารถชำระเงินได้ กรุณาลองอีกครั้ง",
  pdfLoading: "กำลังโหลดไลบรารี PDF กรุณารอ...",
  sampleTitle: "ตัวอย่าง — ตารางการชำระรายเดือน",
  sampleSubtitle: "หน้า 3 จาก 7 · แผนสมบูรณ์รวมหนี้และวิธีทั้งหมด",
  sampleMonth: "เดือน", samplePayment: "การชำระ", samplePrincipal: "เงินต้น",
  sampleInterest: "ดอกเบี้ย", sampleBalance: "ยอดคงเหลือ",
  sampleFullPlan: n => `✦ แผนสมบูรณ์: หนี้ ${n} รายการ · เปรียบเทียบ 4 วิธี · วิเคราะห์การรวมหนี้`,
  getFullPlanBtn: price => `แผน 7 หน้าสมบูรณ์ — ${price}`,
  secureInstant: "ปลอดภัย · ดาวน์โหลดทันที · ไม่ต้องสมัครสมาชิก",
  copyright: "© 2026 Weelco Inc. สงวนลิขสิทธิ์ เพื่อวัตถุประสงค์ด้านข้อมูลเท่านั้น ไม่ใช่คำแนะนำทางการเงิน",
};

const STRINGS = { en, de, fr, es, pt, it, nl, sv, nb, da, fi, pl, cs, hu, ro, sk, sl, et, lv, lt, bg, ja, th };

// Footer / nav translations — merged into every getUiStrings() result as u.nav.*
const NAV_STRINGS = {
  en: {
    calculators: "Calculators",
    debtCalculator: "Debt Payoff Calculator",
    creditCard: "Credit Card Calculator",
    snowball: "Debt Snowball",
    avalanche: "Debt Avalanche",
    repayment: "Debt Repayment",
    studentLoan: "Student Loan Calculator",
    personalLoan: "Personal Loan Calculator",
    carLoan: "Car Loan Calculator",
    resources: "Resources",
    about: "About",
    faq: "FAQ",
    contact: "Contact",
    blog: "Blog",
    legal: "Legal",
    tos: "Terms of Service",
    privacy: "Privacy Policy",
  },
  de: {
    calculators: "Rechner",
    debtCalculator: "Schuldenrechner",
    creditCard: "Kreditkartenrechner",
    snowball: "Schneeball-Methode",
    avalanche: "Lawinen-Methode",
    repayment: "Tilgungsrechner",
    studentLoan: "Studienkreditrechner",
    personalLoan: "Privatkreditrechner",
    carLoan: "Autokreditrechner",
    resources: "Ressourcen",
    about: "Über uns",
    faq: "FAQ",
    contact: "Kontakt",
    blog: "Blog",
    legal: "Rechtliches",
    tos: "Nutzungsbedingungen",
    privacy: "Datenschutz",
  },
  fr: {
    calculators: "Calculateurs",
    debtCalculator: "Calculateur de dettes",
    creditCard: "Calculateur carte de crédit",
    snowball: "Méthode boule de neige",
    avalanche: "Méthode avalanche",
    repayment: "Calculateur de remboursement",
    studentLoan: "Calculateur prêt étudiant",
    personalLoan: "Calculateur prêt personnel",
    carLoan: "Calculateur prêt auto",
    resources: "Ressources",
    about: "À propos",
    faq: "FAQ",
    contact: "Contact",
    blog: "Blog",
    legal: "Légal",
    tos: "Conditions d'utilisation",
    privacy: "Confidentialité",
  },
  es: {
    calculators: "Calculadoras",
    debtCalculator: "Calculadora de deudas",
    creditCard: "Calculadora tarjeta de crédito",
    snowball: "Bola de nieve",
    avalanche: "Método avalancha",
    repayment: "Calculadora de pagos",
    studentLoan: "Calculadora préstamo estudiantil",
    personalLoan: "Calculadora préstamo personal",
    carLoan: "Calculadora préstamo de auto",
    resources: "Recursos",
    about: "Acerca de",
    faq: "FAQ",
    contact: "Contacto",
    blog: "Blog",
    legal: "Legal",
    tos: "Términos de servicio",
    privacy: "Privacidad",
  },
  pt: {
    calculators: "Calculadoras",
    debtCalculator: "Calculadora de dívidas",
    creditCard: "Calculadora cartão de crédito",
    snowball: "Método bola de neve",
    avalanche: "Método avalanche",
    repayment: "Calculadora de pagamento",
    studentLoan: "Calculadora empréstimo estudantil",
    personalLoan: "Calculadora empréstimo pessoal",
    carLoan: "Calculadora financiamento de carro",
    resources: "Recursos",
    about: "Sobre nós",
    faq: "FAQ",
    contact: "Contato",
    blog: "Blog",
    legal: "Legal",
    tos: "Termos de serviço",
    privacy: "Privacidade",
  },
  it: {
    calculators: "Calcolatori",
    debtCalculator: "Calcolatore debiti",
    creditCard: "Calcolatore carta di credito",
    snowball: "Metodo palla di neve",
    avalanche: "Metodo valanga",
    repayment: "Calcolatore rimborso",
    studentLoan: "Calcolatore prestito studentesco",
    personalLoan: "Calcolatore prestito personale",
    carLoan: "Calcolatore prestito auto",
    resources: "Risorse",
    about: "Chi siamo",
    faq: "FAQ",
    contact: "Contatto",
    blog: "Blog",
    legal: "Legale",
    tos: "Termini di servizio",
    privacy: "Privacy",
  },
  nl: {
    calculators: "Calculators",
    debtCalculator: "Schulden calculator",
    creditCard: "Creditcard calculator",
    snowball: "Sneeuwbal methode",
    avalanche: "Lawine methode",
    repayment: "Aflossings calculator",
    studentLoan: "Studielening calculator",
    personalLoan: "Persoonlijke lening calculator",
    carLoan: "Autolening calculator",
    resources: "Bronnen",
    about: "Over ons",
    faq: "FAQ",
    contact: "Contact",
    blog: "Blog",
    legal: "Juridisch",
    tos: "Gebruiksvoorwaarden",
    privacy: "Privacy",
  },
  sv: {
    calculators: "Kalkylatorer",
    debtCalculator: "Skuldkalkylator",
    creditCard: "Kreditkortkalkylator",
    snowball: "Snöbollsmetoden",
    avalanche: "Lavinmetoden",
    repayment: "Amorteringskalkylator",
    studentLoan: "Studielånkalkylator",
    personalLoan: "Privatlånkalkylator",
    carLoan: "Billånkalkylator",
    resources: "Resurser",
    about: "Om oss",
    faq: "FAQ",
    contact: "Kontakt",
    blog: "Blogg",
    legal: "Juridik",
    tos: "Användarvillkor",
    privacy: "Integritet",
  },
  nb: {
    calculators: "Kalkulatorer",
    debtCalculator: "Gjeldskalkulator",
    creditCard: "Kredittkort kalkulator",
    snowball: "Snøballmetoden",
    avalanche: "Skredmetoden",
    repayment: "Nedbetalingskalkulator",
    studentLoan: "Studielån kalkulator",
    personalLoan: "Privatlån kalkulator",
    carLoan: "Billån kalkulator",
    resources: "Ressurser",
    about: "Om oss",
    faq: "FAQ",
    contact: "Kontakt",
    blog: "Blogg",
    legal: "Juridisk",
    tos: "Vilkår for bruk",
    privacy: "Personvern",
  },
  da: {
    calculators: "Beregnere",
    debtCalculator: "Gældsberegner",
    creditCard: "Kreditkort beregner",
    snowball: "Sneboldmetoden",
    avalanche: "Lavinemetoden",
    repayment: "Tilbagebetalingsberegner",
    studentLoan: "Studielån beregner",
    personalLoan: "Privatlån beregner",
    carLoan: "Billån beregner",
    resources: "Ressourcer",
    about: "Om os",
    faq: "FAQ",
    contact: "Kontakt",
    blog: "Blog",
    legal: "Juridisk",
    tos: "Vilkår",
    privacy: "Privatliv",
  },
  fi: {
    calculators: "Laskurit",
    debtCalculator: "Velanmaksulaskuri",
    creditCard: "Luottokorttilaskuri",
    snowball: "Lumipallomenetelmä",
    avalanche: "Lumivyörymenetelmä",
    repayment: "Takaisinmaksulaskuri",
    studentLoan: "Opintolainanlaskuri",
    personalLoan: "Henkilölainanlaskuri",
    carLoan: "Autolainanlaskuri",
    resources: "Resurssit",
    about: "Tietoja",
    faq: "UKK",
    contact: "Yhteystiedot",
    blog: "Blogi",
    legal: "Oikeudellinen",
    tos: "Käyttöehdot",
    privacy: "Tietosuoja",
  },
  pl: {
    calculators: "Kalkulatory",
    debtCalculator: "Kalkulator spłaty długów",
    creditCard: "Kalkulator karty kredytowej",
    snowball: "Metoda śniegowej kuli",
    avalanche: "Metoda lawiny",
    repayment: "Kalkulator spłat",
    studentLoan: "Kalkulator kredytu studenckiego",
    personalLoan: "Kalkulator kredytu osobistego",
    carLoan: "Kalkulator kredytu samochodowego",
    resources: "Zasoby",
    about: "O nas",
    faq: "FAQ",
    contact: "Kontakt",
    blog: "Blog",
    legal: "Prawne",
    tos: "Warunki usługi",
    privacy: "Prywatność",
  },
  cs: {
    calculators: "Kalkulačky",
    debtCalculator: "Kalkulačka splácení dluhů",
    creditCard: "Kalkulačka kreditní karty",
    snowball: "Metoda sněhové koule",
    avalanche: "Lavinová metoda",
    repayment: "Kalkulačka splátek",
    studentLoan: "Kalkulačka studentské půjčky",
    personalLoan: "Kalkulačka osobní půjčky",
    carLoan: "Kalkulačka půjčky na auto",
    resources: "Zdroje",
    about: "O nás",
    faq: "FAQ",
    contact: "Kontakt",
    blog: "Blog",
    legal: "Právní",
    tos: "Podmínky použití",
    privacy: "Soukromí",
  },
  hu: {
    calculators: "Kalkulátorok",
    debtCalculator: "Adósságkalkulátor",
    creditCard: "Hitelkártya kalkulátor",
    snowball: "Hógolyó módszer",
    avalanche: "Lavina módszer",
    repayment: "Törlesztési kalkulátor",
    studentLoan: "Diákhitel kalkulátor",
    personalLoan: "Személyi kölcsön kalkulátor",
    carLoan: "Autóhitel kalkulátor",
    resources: "Erőforrások",
    about: "Rólunk",
    faq: "GYIK",
    contact: "Kapcsolat",
    blog: "Blog",
    legal: "Jogi",
    tos: "Felhasználási feltételek",
    privacy: "Adatvédelem",
  },
  ro: {
    calculators: "Calculatoare",
    debtCalculator: "Calculator datorii",
    creditCard: "Calculator card de credit",
    snowball: "Metoda bulgărelui de zăpadă",
    avalanche: "Metoda avalanșei",
    repayment: "Calculator rambursare",
    studentLoan: "Calculator împrumut studențesc",
    personalLoan: "Calculator împrumut personal",
    carLoan: "Calculator credit auto",
    resources: "Resurse",
    about: "Despre noi",
    faq: "FAQ",
    contact: "Contact",
    blog: "Blog",
    legal: "Legal",
    tos: "Termeni și condiții",
    privacy: "Confidențialitate",
  },
  sk: {
    calculators: "Kalkulačky",
    debtCalculator: "Kalkulačka splácania dlhov",
    creditCard: "Kalkulačka kreditnej karty",
    snowball: "Metóda snehových gúľ",
    avalanche: "Lavínová metóda",
    repayment: "Kalkulačka splátok",
    studentLoan: "Kalkulačka študentskej pôžičky",
    personalLoan: "Kalkulačka osobnej pôžičky",
    carLoan: "Kalkulačka pôžičky na auto",
    resources: "Zdroje",
    about: "O nás",
    faq: "FAQ",
    contact: "Kontakt",
    blog: "Blog",
    legal: "Právne",
    tos: "Podmienky používania",
    privacy: "Ochrana súkromia",
  },
  sl: {
    calculators: "Kalkulatorji",
    debtCalculator: "Kalkulator dolga",
    creditCard: "Kalkulator kreditne kartice",
    snowball: "Metoda snežne kepe",
    avalanche: "Metoda plazu",
    repayment: "Kalkulator odplačila",
    studentLoan: "Kalkulator študentskega posojila",
    personalLoan: "Kalkulator osebnega posojila",
    carLoan: "Kalkulator avtomobilskega posojila",
    resources: "Viri",
    about: "O nas",
    faq: "FAQ",
    contact: "Kontakt",
    blog: "Blog",
    legal: "Pravno",
    tos: "Pogoji storitve",
    privacy: "Zasebnost",
  },
  et: {
    calculators: "Kalkulaatorid",
    debtCalculator: "Võlakalkulaator",
    creditCard: "Krediitkaardi kalkulaator",
    snowball: "Lumepalli meetod",
    avalanche: "Laviini meetod",
    repayment: "Tagasimaksekalkulaator",
    studentLoan: "Õppelaenu kalkulaator",
    personalLoan: "Isikliku laenu kalkulaator",
    carLoan: "Autolaenu kalkulaator",
    resources: "Ressursid",
    about: "Meist",
    faq: "KKK",
    contact: "Kontakt",
    blog: "Blogi",
    legal: "Juriidiline",
    tos: "Teenusetingimused",
    privacy: "Privaatsus",
  },
  lv: {
    calculators: "Kalkulatori",
    debtCalculator: "Parādu kalkulators",
    creditCard: "Kredītkartes kalkulators",
    snowball: "Sniega bumbas metode",
    avalanche: "Lavīnas metode",
    repayment: "Atmaksas kalkulators",
    studentLoan: "Studiju kredīta kalkulators",
    personalLoan: "Personīgā aizdevuma kalkulators",
    carLoan: "Auto kredīta kalkulators",
    resources: "Resursi",
    about: "Par mums",
    faq: "BUJ",
    contact: "Kontakti",
    blog: "Blogs",
    legal: "Juridiski",
    tos: "Pakalpojumu noteikumi",
    privacy: "Privātums",
  },
  lt: {
    calculators: "Skaičiuoklės",
    debtCalculator: "Skolų skaičiuoklė",
    creditCard: "Kreditinės kortelės skaičiuoklė",
    snowball: "Sniego gniūžtės metodas",
    avalanche: "Lavinos metodas",
    repayment: "Grąžinimo skaičiuoklė",
    studentLoan: "Studijų paskolos skaičiuoklė",
    personalLoan: "Asmeninės paskolos skaičiuoklė",
    carLoan: "Automobilio paskolos skaičiuoklė",
    resources: "Ištekliai",
    about: "Apie mus",
    faq: "DUK",
    contact: "Kontaktai",
    blog: "Tinklaraštis",
    legal: "Teisinė",
    tos: "Paslaugų sąlygos",
    privacy: "Privatumas",
  },
  bg: {
    calculators: "Калкулатори",
    debtCalculator: "Калкулатор за дългове",
    creditCard: "Калкулатор кредитна карта",
    snowball: "Метод снежна топка",
    avalanche: "Метод лавина",
    repayment: "Калкулатор за изплащане",
    studentLoan: "Калкулатор студентски кредит",
    personalLoan: "Калкулатор личен кредит",
    carLoan: "Калкулатор автокредит",
    resources: "Ресурси",
    about: "За нас",
    faq: "ЧЗВ",
    contact: "Контакт",
    blog: "Блог",
    legal: "Правно",
    tos: "Условия за ползване",
    privacy: "Поверителност",
  },
  ja: {
    calculators: "計算機",
    debtCalculator: "借金返済計算機",
    creditCard: "クレジットカード計算機",
    snowball: "雪だるま式返済",
    avalanche: "アバランチ返済",
    repayment: "返済計算機",
    studentLoan: "学生ローン計算機",
    personalLoan: "個人ローン計算機",
    carLoan: "自動車ローン計算機",
    resources: "リソース",
    about: "概要",
    faq: "よくある質問",
    contact: "お問い合わせ",
    blog: "ブログ",
    legal: "法的事項",
    tos: "利用規約",
    privacy: "プライバシー",
  },
  th: {
    calculators: "เครื่องคำนวณ",
    debtCalculator: "เครื่องคำนวณหนี้",
    creditCard: "เครื่องคำนวณบัตรเครดิต",
    snowball: "วิธีก้อนหิมะ",
    avalanche: "วิธีหิมะถล่ม",
    repayment: "เครื่องคำนวณการชำระหนี้",
    studentLoan: "เครื่องคำนวณสินเชื่อนักเรียน",
    personalLoan: "เครื่องคำนวณสินเชื่อบุคคล",
    carLoan: "เครื่องคำนวณสินเชื่อรถยนต์",
    resources: "ทรัพยากร",
    about: "เกี่ยวกับเรา",
    faq: "คำถามที่พบบ่อย",
    contact: "ติดต่อ",
    blog: "บล็อก",
    legal: "กฎหมาย",
    tos: "ข้อกำหนดบริการ",
    privacy: "ความเป็นส่วนตัว",
  },
};
